import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Card, Button, Badge, Row, Col, Spinner, Nav, Tab, Form, Alert, ListGroup, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    updateTrip, getDrivers, uploadMonthlySettlementInvoice,
    getTripBookingChatMessages, sendTripBookingChatMessage, getDriverUnreadBookingChats, translateWhatsAppText
} from '../api';
import apiClient from '../../api'; 
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/useAuthStore';
import { 
    MapPin, Navigation, Clock, Calendar, 
    LogOut, Car, ChevronRight, Users,
    XCircle, Check, Bell, Phone, MessageCircle, ArrowLeftRight, FileText,
    AlertTriangle, Crosshair, Timer, BellOff, UserCircle,
    User, Mail, Smartphone, Zap, Camera, CheckCircle, Trash2, Send, Globe
} from 'lucide-react';
import DeleteAccountModal from '../components/DeleteAccountModal';
import DriverAvailabilityModal from './DriverAvailabilityModal';
import { GoogleMap, DirectionsService, DirectionsRenderer, MarkerF } from '@react-google-maps/api';
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { toast } from 'react-toastify';
import NativeBridge from '../../utils/NativeBridge';
import { requestForToken, onMessageListener, getPendingPushAction } from '../../firebase';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { LanguageProvider, useLanguage, LanguageSelector } from '../context/LanguageContext';

const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');
const APP_VERSION = "0.1.101";

const toTitleCase = (str) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getTomorrowDateStr = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const isTripAllowedForEnRoute = (scheduledTimeStr) => {
    if (!scheduledTimeStr) return true;
    try {
        const tripDate = new Date(scheduledTimeStr);
        if (isNaN(tripDate.getTime())) return true;
        const now = new Date();
        const diffMinutes = (tripDate.getTime() - now.getTime()) / 60000;
        // Permitido únicamente si faltan 120 minutos (2 horas) o menos para la reserva (o si ya es la hora/pasado)
        return diffMinutes <= 120;
    } catch (e) {
        return true;
    }
};

// --- TRADUCCIÓN Y COLORES DE ESTADOS ---
const getStatusBadgeProps = (status, t) => {
    switch (status?.toLowerCase()) {
        case 'pending':
            return { text: t ? t('status_pending') : 'Pendiente', bgClass: 'secondary' };
        case 'assigned':
        case 'confirmed':
            return { text: t ? t('status_confirmed') : 'Confirmado', bgClass: 'warning' };
        case 'en_route':
            return { text: t ? t('status_en_route') : 'En Camino', bgClass: 'warning' };
        case 'arrived':
            return { text: t ? t('status_arrived') : 'En Posición', bgClass: 'info' };
        case 'on_board':
            return { text: t ? t('status_on_board') : 'A Bordo / En Ruta', bgClass: 'danger' };
        case 'completed':
            return { text: t ? t('status_completed') : 'Finalizado', bgClass: 'success' };
        case 'cancelled':
            return { text: t ? t('status_cancelled') : 'Cancelado', bgClass: 'dark' };
        case 'cancelled_paid':
            return { text: t ? t('status_cancelled_paid') : 'Cancelado con Cargo', bgClass: 'danger' };
        case 'no_show':
            return { text: t ? t('status_no_show') : 'No Show', bgClass: 'dark' };
        default:
            return { text: status?.toUpperCase(), bgClass: 'secondary' };
    }
};

// --- FORMATO DE MINUTOS DE ESPERA ---
const formatWaitingMinutes = (minutes) => {
    if (!minutes) return "0 Min";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) {
        return mins > 0 ? `${hrs} Hrs ${mins} Min` : `${hrs} Hrs`;
    }
    return `${mins} Min`;
};

// --- VISIBILIDAD DE TIEMPO DE ESPERA Y GANANCIAS (OPCIÓN B) ---
// La espera (minutos y pago) solo es visible para el conductor una vez que el voucher fue procesado/liquidado.
const isTripVoucherProcessed = (trip) => {
    const adminStatus = String(trip?.admin_status || '').toLowerCase();
    return adminStatus === 'processed' || adminStatus === 'settled';
};

const getDriverVisibleEarnings = (trip) => {
    if (!trip) return 0;
    const rawEarnings = Number(trip.driver_earnings || 0);
    const waitCost = Number(trip.waiting_time_provider_cost || 0);
    if (!isTripVoucherProcessed(trip) && waitCost > 0) {
        const baseCost = Number(trip.provider_cost_base || 0);
        if (baseCost > 0 && rawEarnings < baseCost) {
            const totalBase = baseCost + waitCost;
            const ratio = totalBase > 0 ? (rawEarnings / totalBase) : 1;
            return Math.max(0, Math.round(baseCost * ratio));
        }
        return Math.max(0, Math.round(rawEarnings - waitCost));
    }
    return Math.round(rawEarnings);
};

// --- CRONÓMETRO DE ESPERA EN VIVO ---
const WaitingTimer = ({ trip, scheduledTimeStr, gpsArrivedTimeStr }) => {
    const { t } = useLanguage();
    const [elapsedText, setElapsedText] = useState("00:00");
    const [isCounting, setIsCounting] = useState(false);

    useEffect(() => {
        if (!gpsArrivedTimeStr) return;

        const scheduledTime = new Date(scheduledTimeStr);
        const arrivedTime = new Date(gpsArrivedTimeStr);

        // Detectar cortesía igual que el backend (Aeropuerto: 60 min, Otros: cortesía cliente o 5 min)
        const addrLower = String(trip?.pickup_address || '').toLowerCase();
        const airportKeywords = [
            "aeropuerto", "airport", "arturo merino benitez", "pudahuel",
            "chacalluta", "el tepual", "carriel sur", "el plumerillo",
            "ezeiza", "aeroparque", "terminal 1", "terminal 2"
        ];
        const iataRegex = /\b(scl|amb|ari|pmc|cjc|anf|iqq|ccp|zco|lsc|lim|puj|sdq|eze|aep|bog|cpo|araucania)\b/;
        const isAirport = airportKeywords.some(k => addrLower.includes(k)) || iataRegex.test(addrLower);
        const courtesyMin = isAirport ? 60 : (Number(trip?.client?.courtesy_wait_minutes) || 5);

        // Base: máximo entre la llegada real y la hora programada + minutos de cortesía
        const baseStartTime = arrivedTime.getTime() > scheduledTime.getTime() 
            ? arrivedTime 
            : scheduledTime;
        const effectiveStartTime = new Date(baseStartTime.getTime() + courtesyMin * 60 * 1000);

        const updateTimer = () => {
            // Si el auto ya inició marcha (>500m del origen), congelar el reloj en ese instante
            const referenceNow = trip?.first_movement_at ? new Date(trip.first_movement_at) : new Date();
            const diffMs = referenceNow.getTime() - effectiveStartTime.getTime();
            if (diffMs <= 0) {
                setIsCounting(false);
                const timeText = effectiveStartTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                setElapsedText(`00:00 (Inicia a las ${timeText})`);
                return;
            }

            setIsCounting(true);
            const totalSecs = Math.floor(diffMs / 1000);
            const hours = Math.floor(totalSecs / 3600);
            const mins = Math.floor((totalSecs % 3600) / 60);
            const secs = totalSecs % 60;

            const formatNum = (n) => String(n).padStart(2, '0');

            if (hours > 0) {
                setElapsedText(`${hours} Hr ${formatNum(mins)} min`);
            } else {
                setElapsedText(`${formatNum(mins)}:${formatNum(secs)}`);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [trip, scheduledTimeStr, gpsArrivedTimeStr]);

    return (
        <div className={`d-flex flex-column align-items-center justify-content-center p-3 mb-3 rounded border text-center shadow-sm ${isCounting ? 'bg-info bg-opacity-10 border-info text-info-emphasis' : 'bg-light border-secondary text-muted'}`} style={{ transition: 'all 0.3s ease' }}>
            <span className="small fw-bold text-uppercase letter-spacing-1 mb-1" style={{ fontSize: '10px' }}>
                ⏳ {t('waiting_time_caps')} DEL PASAJERO
            </span>
            <span className="fs-3 fw-bold font-monospace animate-pulse">{elapsedText}</span>
        </div>
    );
};

// Centro por defecto: Santiago, Chile (Cerca del Aeropuerto como fallback más sensato)
const DEFAULT_CENTER = { lat: -33.3928, lng: -70.7925 };

// --- COMPONENTE: MANUAL DE CONFIGURACIÓN ---
const SetupGuideModal = ({ show, onHide }) => {
    const { t } = useLanguage();
    return (
    <Modal show={show} onHide={onHide} centered scrollable>
        <Modal.Header closeButton className="bg-dark text-white border-0">
            <Modal.Title className="h6 fw-bold d-flex align-items-center gap-2">
                <Bell size={20} className="text-warning"/> {t('setup_guide_title')}
            </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light p-0">
            <div className="p-3 bg-white border-bottom mb-2 text-center">
                <p className="small text-muted mb-0">{t('setup_guide_subtitle')}</p>
            </div>
            <div className="p-3">
                <div className="mb-4">
                    <h6 className="fw-bold text-primary border-bottom pb-2">{t('setup_battery_title')}</h6>
                    <ol className="small mt-2">
                        <li className="mb-2">{t('setup_battery_step1')}</li>
                        <li className="mb-2">{t('setup_battery_step2')}</li>
                        <li className="mb-2">{t('setup_battery_step3')}</li>
                        <li className="mb-2 text-danger fw-bold">{t('setup_battery_step4')}</li>
                    </ol>
                    <div className="bg-warning bg-opacity-10 p-2 rounded border border-warning border-opacity-25 mt-1">
                        <small className="text-warning-emphasis fw-bold">{t('setup_why')}</small>
                        <small className="text-muted">{t('setup_battery_desc')}</small>
                    </div>
                </div>

                <div className="mb-4">
                    <h6 className="fw-bold text-primary border-bottom pb-2">{t('setup_data_title')}</h6>
                    <ol className="small mt-2">
                        <li className="mb-2">{t('setup_data_step1')}</li>
                        <li className="mb-2">{t('setup_data_step2')}</li>
                        <li className="mb-2">{t('setup_data_step3')}</li>
                    </ol>
                </div>

                <div>
                    <h6 className="fw-bold text-primary border-bottom pb-2">{t('setup_notif_title')}</h6>
                    <ol className="small mt-2">
                        <li className="mb-2">{t('setup_notif_step1')}</li>
                        <li className="mb-2">{t('setup_notif_step2')}</li>
                    </ol>
                </div>
            </div>
        </Modal.Body>
        <Modal.Footer className="bg-white border-0">
            <Button variant="primary" className="w-100 fw-bold py-2" onClick={onHide}>{t('setup_btn_understood')}</Button>
        </Modal.Footer>
    </Modal>
    );
};

// --- COMPONENTE: MODAL DE PERMISOS NATIVOS ---
const PermissionsCheckModal = ({ show, onActivate }) => {
    const { t } = useLanguage();
    return (
    <Modal show={show} backdrop="static" keyboard={false} centered>
        <Modal.Header className="bg-primary text-white border-0">
            <Modal.Title className="h5 fw-bold d-flex align-items-center gap-2">
                <Zap size={22} className="text-warning"/> {t('perm_title')}
            </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
            <div className="text-center mb-4">
                <div className="bg-primary bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                    <Navigation size={40} className="text-primary"/>
                </div>
                <h5>{t('perm_greeting')}</h5>
                <p className="text-muted small">{t('perm_desc')}</p>
            </div>
            
            <div className="bg-light p-3 rounded mb-3">
                <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="bg-white p-2 rounded border shadow-sm"><MapPin size={20} className="text-success"/></div>
                    <div>
                        <div className="fw-bold small">{t('perm_gps_title')}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t('perm_gps_desc')}</div>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="bg-white p-2 rounded border shadow-sm"><Bell size={20} className="text-primary"/></div>
                    <div>
                        <div className="fw-bold small">{t('perm_notif_title')}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t('perm_notif_desc')}</div>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="bg-white p-2 rounded border shadow-sm"><Zap size={20} className="text-warning"/></div>
                    <div>
                        <div className="fw-bold small">{t('perm_battery_title')}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t('perm_battery_desc')}</div>
                    </div>
                </div>
            </div>

            <Alert variant="warning" className="border-0 small mb-0 py-2">
                <AlertTriangle size={14} className="me-2"/> 
                {t('perm_warning')}
            </Alert>
        </Modal.Body>
        <Modal.Footer className="border-0 p-3">
            <Button variant="primary" className="w-100 fw-bold py-3 shadow" onClick={onActivate}>
                {t('perm_btn_configure')}
            </Button>
        </Modal.Footer>
    </Modal>
    );
};

const DriverPortal = ({ isLoaded }) => {
    const { user, logout } = useAuthStore();
    const navigate = useNavigate();
    const { t, language } = useLanguage();

    useEffect(() => {
        const checkApprovalStatus = async () => {
            try {
                const res = await apiClient.get('/transport/drivers/me/status');
                if (res.data && res.data.is_approved === false) {
                    navigate('/onboarding');
                }
                if (res.data && typeof res.data.show_pricing === 'boolean') {
                    setDriverInfo(prev => prev ? { ...prev, show_pricing: res.data.show_pricing } : { show_pricing: res.data.show_pricing });
                }
            } catch (err) {
                console.error("Error checking driver approval:", err);
            }
        };
        checkApprovalStatus();
    }, [navigate]);

    const [trips, setTrips] = useState([]);
    const [activeTab, setActiveTab] = useState('new');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tabParam = params.get('tab');
        if (tabParam && ['new', 'agenda', 'tomorrow', 'pagos', 'done'].includes(tabParam)) {
            setActiveTab(tabParam);
        }
    }, []);
    const [driverInfo, setDriverInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState(null); // Nuevo estado para errores de carga
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [directions, setDirections] = useState(null);
    const [currentPos, setCurrentPos] = useState(null);
    const [driverComment, setDriverComment] = useState("");
    const [pushEnabled, setPushEnabled] = useState(null); // null: cargando, true: ok, false: bloqueado
    const [showSetupGuide, setShowSetupGuide] = useState(false);
    const [showPermissionsModal, setShowPermissionsModal] = useState(false);
    const [inAppAlert, setInAppAlert] = useState(null);
    const [confirmBoardTrip, setConfirmBoardTrip] = useState(null); // { tripId, firstMovementAt }
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
    const [earningsView, setEarningsView] = useState(null); // null, 'today', or 'month'

    const [otaVersion, setOtaVersion] = useState(null);
    const [capgoError, setCapgoError] = useState(null);
    const [profileImageUrl, setProfileImageUrl] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [showLocationDisclosure, setShowLocationDisclosure] = useState(false);

    useEffect(() => {
        // En plataformas nativas o emuladores, si no ha otorgado permisos, abrir la divulgacion
        if (Capacitor.isNativePlatform()) {
            NativeBridge.checkPermissions().then(status => {
                if (status.gps !== 'granted') {
                    setShowLocationDisclosure(true);
                }
            }).catch(() => setShowLocationDisclosure(true));
        } else {
            const hasAcceptedDisclosure = localStorage.getItem('gps_disclosure_accepted');
            if (!hasAcceptedDisclosure) {
                setShowLocationDisclosure(true);
            }
        }
    }, []);

    const handleAcceptGpsDisclosure = () => {
        localStorage.setItem('gps_disclosure_accepted', 'true');
        setShowLocationDisclosure(false);
        if (Capacitor.isNativePlatform()) {
            NativeBridge.requestLocationPermission().catch(err => console.log("Permission requested:", err));
        }
    };

    const fetchDriverStatus = async () => {
        try {
            const res = await apiClient.get('/transport/drivers/me/status');
            if (res.data && res.data.profile_image_url) {
                setProfileImageUrl(res.data.profile_image_url);
            }
        } catch (err) {
            console.error("Error al cargar status de conductor:", err);
        }
    };

    useEffect(() => {
        if (user?.linked_driver_id) {
            fetchDriverStatus();
        }
    }, [user]);

    const handleUploadProfilePicture = async (fileBlob) => {
        setUploadingPhoto(true);
        const formData = new FormData();
        formData.append('file', fileBlob, 'profile.jpg');
        
        try {
            const res = await apiClient.post('/transport/drivers/me/profile-picture', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success("Foto de perfil actualizada con éxito.");
            if (res.data && res.data.profile_image_url) {
                setProfileImageUrl(res.data.profile_image_url);
            }
        } catch (err) {
            console.error("Error al subir foto de perfil:", err);
            toast.error("Error al subir la foto de perfil. Intente nuevamente.");
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleSelectPhoto = async () => {
        if (Capacitor.isNativePlatform()) {
            try {
                const image = await CapCamera.getPhoto({
                    quality: 85,
                    allowEditing: true,
                    resultType: CameraResultType.Uri,
                    source: CameraSource.Prompt,
                    promptLabelHeader: 'Foto de Perfil',
                    promptLabelPhoto: 'Elegir de la Galería 🖼️',
                    promptLabelPicture: 'Tomar una Selfie 📸'
                });
                
                const response = await fetch(image.webPath);
                const blob = await response.blob();
                await handleUploadProfilePicture(blob);
            } catch (err) {
                if (err.message !== 'User cancelled photos app') {
                    console.error("Error con Capacitor Camera:", err);
                    toast.error("Error al acceder a la cámara o galería.");
                }
            }
        } else {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = 'image/*';
            fileInput.onchange = async (e) => {
                const file = e.target.files[0];
                if (file) {
                    await handleUploadProfilePicture(file);
                }
            };
            fileInput.click();
        }
    };

    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            CapacitorUpdater.current()
                .then(res => {
                    if (res && res.bundle) {
                        setOtaVersion(res.bundle.version);
                    } else {
                        setOtaVersion("Nativo (sin OTA)");
                    }
                })
                .catch(err => {
                    setCapgoError(err.message || String(err));
                    console.error("Error al obtener versión Capgo:", err);
                });
        } else {
            setOtaVersion("Navegador");
        }
    }, []);

    const [seenNewTrips, setSeenNewTrips] = useState(() => {
        try {
            const saved = localStorage.getItem('seenNewTrips');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });

    const [seenAgendaTrips, setSeenAgendaTrips] = useState(() => {
        try {
            const saved = localStorage.getItem('seenAgendaTrips');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });

    const markTripAsSeen = useCallback((tripId, tabKey) => {
        if (tabKey === 'new') {
            setSeenNewTrips(prev => {
                if (prev.includes(tripId)) return prev;
                const next = [...prev, tripId];
                localStorage.setItem('seenNewTrips', JSON.stringify(next));
                return next;
            });
        } else if (tabKey === 'agenda') {
            setSeenAgendaTrips(prev => {
                if (prev.includes(tripId)) return prev;
                const next = [...prev, tripId];
                localStorage.setItem('seenAgendaTrips', JSON.stringify(next));
                return next;
            });
        }
    }, []);

    // Limpieza de IDs antiguos en localStorage (Garbage Collection)
    useEffect(() => {
        if (trips.length > 0) {
            const tripIds = new Set(trips.map(t => t.id));
            
            setSeenNewTrips(prev => {
                const filtered = prev.filter(id => tripIds.has(id));
                if (filtered.length !== prev.length) {
                    localStorage.setItem('seenNewTrips', JSON.stringify(filtered));
                    return filtered;
                }
                return prev;
            });

            setSeenAgendaTrips(prev => {
                const filtered = prev.filter(id => tripIds.has(id));
                if (filtered.length !== prev.length) {
                    localStorage.setItem('seenAgendaTrips', JSON.stringify(filtered));
                    return filtered;
                }
                return prev;
            });
        }
    }, [trips]);

    const unseenNewCount = useMemo(() => {
        return trips.filter(t => 
            ['assigned', 'pending'].includes(t.status?.toLowerCase()) && 
            !seenNewTrips.includes(t.id)
        ).length;
    }, [trips, seenNewTrips]);

    const unseenAgendaCount = useMemo(() => {
        return trips.filter(t => 
            ['confirmed', 'en_route', 'arrived', 'on_board'].includes(t.status?.toLowerCase()) && 
            !seenAgendaTrips.includes(t.id)
        ).length;
    }, [trips, seenAgendaTrips]);

    // Calcular fecha de mañana de forma centralizada en el componente
    const tomorrowDateStr = getTomorrowDateStr();

    const tomorrowTrips = useMemo(() => {
        return trips.filter(t => {
            if (!t.scheduled_time) return false;
            const dateObj = new Date(t.scheduled_time);
            const y = dateObj.getFullYear();
            const m = String(dateObj.getMonth() + 1).padStart(2, '0');
            const d = String(dateObj.getDate()).padStart(2, '0');
            const tripDateStr = `${y}-${m}-${d}`;
            return tripDateStr === tomorrowDateStr && !['cancelled', 'no_show'].includes(t.status?.toLowerCase());
        });
    }, [trips, tomorrowDateStr]);
    
    // Estados añadidos para control de mapa y envío de estado
    const [showMap, setShowMap] = useState(false); // El mapa inicia OCULTO para maximizar espacio
    const [isSubmittingAction, setIsSubmittingAction] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [completionNotes, setCompletionNotes] = useState("");
    const [tomorrowAck, setTomorrowAck] = useState({ acknowledged: false, acknowledged_at: null, was_sent: false, target_date: null });
    const [isAcknowledgeSubmitting, setIsAcknowledgeSubmitting] = useState(false);

    // Mantener selectedTrip sincronizado con el listado principal de trips
    useEffect(() => {
        if (selectedTrip) {
            const fresh = trips.find(t => t.id === selectedTrip.id);
            if (fresh) {
                if (
                    fresh.status !== selectedTrip.status ||
                    fresh.gps_arrived_time !== selectedTrip.gps_arrived_time ||
                    fresh.start_time !== selectedTrip.start_time ||
                    fresh.end_time !== selectedTrip.end_time ||
                    fresh.first_movement_at !== selectedTrip.first_movement_at ||
                    fresh.driver_earnings !== selectedTrip.driver_earnings ||
                    fresh.admin_status !== selectedTrip.admin_status ||
                    fresh.waiting_time_minutes !== selectedTrip.waiting_time_minutes ||
                    fresh.waiting_time_provider_cost !== selectedTrip.waiting_time_provider_cost ||
                    JSON.stringify(fresh.stops) !== JSON.stringify(selectedTrip.stops) ||
                    JSON.stringify(fresh.extra_services) !== JSON.stringify(selectedTrip.extra_services)
                ) {
                    setSelectedTrip(fresh);
                }
            }
        }
    }, [trips, selectedTrip]);

    const handleConfirmTomorrowAgenda = async () => {
        setIsAcknowledgeSubmitting(true);
        try {
            // Calcular fecha de mañana local como fallback
            const today = new Date();
            const tomorrow = new Date(today);
            tomorrow.setDate(today.getDate() + 1);
            const year = tomorrow.getFullYear();
            const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
            const day = String(tomorrow.getDate()).padStart(2, '0');
            const tomorrowDateStr = `${year}-${month}-${day}`;

            // Usar la fecha del servidor si está disponible para evitar desfases de zona horaria
            const dateToConfirm = tomorrowAck.target_date || tomorrowDateStr;

            await apiClient.post('/transport/drivers/notifications/acknowledge', { date: dateToConfirm });
            
            // Recargar confirmación
            const ackRes = await apiClient.get('/transport/drivers/notifications/tomorrow-acknowledgment');
            setTomorrowAck({
                acknowledged: ackRes.data.acknowledged,
                acknowledged_at: ackRes.data.acknowledged_at,
                was_sent: ackRes.data.was_sent,
                target_date: ackRes.data.target_date
            });
            alert("✓ Confirmaste la lectura de tu agenda de mañana exitosamente.");
        } catch (e) {
            console.error("Error confirming tomorrow agenda", e);
            alert("Ocurrió un error al confirmar la agenda. Por favor intenta nuevamente.");
        } finally {
            setIsAcknowledgeSubmitting(false);
        }
    };

    const handleWhatsAppClick = (passengerName, phone, scheduledTime, tripId, timezone, clientId) => {
        if (!phone) return;
        let cleanPhone = phone.replace(/[^0-9]/g, '');
        if (!cleanPhone.startsWith('56') && cleanPhone.length === 9) {
            cleanPhone = '56' + cleanPhone;
        }
        
        let formattedTime = "";
        let formattedDate = "";
        if (scheduledTime) {
            const dateObj = new Date(scheduledTime);
            if (!isNaN(dateObj.getTime())) {
                const tz = timezone || 'America/Santiago';
                try {
                    const day = new Intl.DateTimeFormat('es-CL', { timeZone: tz, day: '2-digit' }).format(dateObj);
                    const month = new Intl.DateTimeFormat('es-CL', { timeZone: tz, month: '2-digit' }).format(dateObj);
                    const year = new Intl.DateTimeFormat('es-CL', { timeZone: tz, year: 'numeric' }).format(dateObj);
                    formattedDate = `${day}/${month}/${year}`;
                    
                    formattedTime = new Intl.DateTimeFormat('es-CL', {
                        timeZone: tz,
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    }).format(dateObj);
                } catch (e) {
                    console.error("Error formatting timezone-safe date/time:", e);
                    const day = dateObj.getDate().toString().padStart(2, '0');
                    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
                    const year = dateObj.getFullYear();
                    formattedDate = `${day}/${month}/${year}`;
                    formattedTime = dateObj.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', hour12: false});
                }
            }
        }
        
        const formattedName = toTitleCase(passengerName);
        const isMasterPeru = (clientId === 94);
        
        let greeting = "";
        if (user?.full_name) {
            greeting = isMasterPeru 
                ? `le saluda su conductor ${toTitleCase(user.full_name)}` 
                : `le saluda su conductor ${toTitleCase(user.full_name)} de Transportes Emilia`;
        } else {
            greeting = isMasterPeru 
                ? `le saluda su conductor` 
                : `le saluda su conductor de Transportes Emilia`;
        }
        
        const serviceText = tripId ? `servicio #${tripId}` : 'servicio';
        const text = `Hola ${formattedName}, ${greeting} asignado para su ${serviceText} del día ${formattedDate} a las ${formattedTime} hrs. No dude en contactarme si lo necesita. Atte.`;
        const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
    };
    
    // ESTADOS PARA LIQUIDACIONES Y FILTROS
    const [settlementsData, setSettlementsData] = useState({ personal: [], fleet_vehicles: [], is_owner: false });
    const [loadingSettlements, setLoadingSettlements] = useState(false);
    const [historyMonth, setHistoryMonth] = useState(new Date().getMonth());
    const [settlementsMonth, setSettlementsMonth] = useState(new Date().getMonth());
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [fetchedMonths, setFetchedMonths] = useState(new Set([new Date().getMonth()]));

    // NUEVOS ESTADOS
    const [isOnline, setIsOnline] = useState(false);
    const [isSendingPanic, setIsSendingPanic] = useState(false);
    const [isCapturingEvidence, setIsCapturingEvidence] = useState(false);
    
    // SELECCIÓN DE VEHÍCULO
    const [showVehicleSelection, setShowVehicleSelection] = useState(false);
    const [isAssigningVehicle, setIsAssigningVehicle] = useState(false);

    // PERFIL
    const [showProfile, setShowProfile] = useState(false);
    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);

    // BOOKING.COM LIVE CHAT (CONDUCTOR <-> PASAJERO)
    const [bookingUnreadMap, setBookingUnreadMap] = useState({});
    const [bookingChatTrip, setBookingChatTrip] = useState(null);
    const [bookingChatMessages, setBookingChatMessages] = useState([]);
    const [bookingChatLoading, setBookingChatLoading] = useState(false);
    const [bookingChatText, setBookingChatText] = useState('');
    const [bookingChatSending, setBookingChatSending] = useState(false);
    const [bookingChatTranslations, setBookingChatTranslations] = useState({});
    const [bookingTranslatingId, setBookingTranslatingId] = useState(null);
    const [bookingTranslatingDraft, setBookingTranslatingDraft] = useState(false);
    const bookingChatEndRef = useRef(null);

    const isBookingComTrip = useCallback((tripObj) => {
        if (!tripObj) return false;
        const clientName = String(tripObj.client?.name || '').toLowerCase();
        return Boolean(tripObj.external_reference) || clientName.includes('booking');
    }, []);

    const openBookingChatModal = useCallback(async (tripObj, e = null) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (!tripObj) return;
        setBookingChatTrip(tripObj);
        setBookingChatLoading(true);
        try {
            const res = await getTripBookingChatMessages(tripObj.id, false);
            setBookingChatMessages(res?.messages || []);
            setBookingUnreadMap(prev => {
                const copy = { ...prev };
                delete copy[String(tripObj.id)];
                delete copy[tripObj.id];
                return copy;
            });
            setTimeout(() => bookingChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 150);
        } catch (err) {
            toast.error("No se pudo cargar el chat de Booking.com.");
        } finally {
            setBookingChatLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!bookingChatTrip?.id) return;
        const interval = setInterval(async () => {
            if (document.hidden) return;
            try {
                const res = await getTripBookingChatMessages(bookingChatTrip.id, false);
                if (res?.messages) {
                    setBookingChatMessages(res.messages);
                }
            } catch (e) {
                // silencioso en polling
            }
        }, 7000);
        return () => clearInterval(interval);
    }, [bookingChatTrip?.id]);

    const handleSendDriverBookingChat = async (e, quickText = null) => {
        if (e && e.preventDefault) e.preventDefault();
        const textToSend = (quickText !== null ? quickText : bookingChatText).trim();
        if (!textToSend || !bookingChatTrip) return;
        setBookingChatSending(true);
        try {
            const dName = driverInfo?.name || user?.full_name || user?.name || 'Conductor';
            const res = await sendTripBookingChatMessage(bookingChatTrip.id, textToSend, 'DRIVER', dName);
            if (res && res.status === 'success') {
                if (quickText === null) setBookingChatText('');
                const refreshed = await getTripBookingChatMessages(bookingChatTrip.id, false);
                setBookingChatMessages(refreshed?.messages || []);
                setTimeout(() => bookingChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            }
        } catch (err) {
            // manejado en wrapper
        } finally {
            setBookingChatSending(false);
        }
    };

    const handleTranslateDriverChatMessage = async (msgId, rawText) => {
        if (!rawText || !rawText.trim()) return;
        if (bookingChatTranslations[msgId]) {
            setBookingChatTranslations(prev => {
                const copy = { ...prev };
                delete copy[msgId];
                return copy;
            });
            return;
        }
        try {
            setBookingTranslatingId(msgId);
            const targetLang = language === 'pt' ? 'pt' : language === 'en' ? 'en' : 'es';
            const res = await translateWhatsAppText(rawText, targetLang, bookingChatTrip?.passenger_phone || null);
            if (res && res.translated_text) {
                setBookingChatTranslations(prev => ({
                    ...prev,
                    [msgId]: {
                        text: res.translated_text,
                        sourceLang: res.detected_source_language || ''
                    }
                }));
            }
        } catch (e) {
            // manejado en wrapper
        } finally {
            setBookingTranslatingId(null);
        }
    };

    const handleTranslateDriverDraft = async () => {
        if (!bookingChatText.trim()) return;
        try {
            setBookingTranslatingDraft(true);
            const res = await translateWhatsAppText(
                bookingChatText.trim(),
                'auto_passenger',
                bookingChatTrip?.passenger_phone || null
            );
            if (res && res.translated_text) {
                setBookingChatText(res.translated_text);
                toast.success(`Traducido a ${res.target_language_name || 'idioma del pasajero'}`);
            }
        } catch (e) {
            // manejado en wrapper
        } finally {
            setBookingTranslatingDraft(false);
        }
    };

    const renderDriverBookingChatModal = () => (
        <Modal
            show={!!bookingChatTrip}
            onHide={() => !bookingChatSending && setBookingChatTrip(null)}
            centered
            size="md"
            contentClassName="border-0 rounded-4 shadow-lg overflow-hidden"
        >
            <Modal.Header
                closeButton
                closeVariant="white"
                style={{ backgroundColor: '#003580', color: '#ffffff', borderBottom: '1px solid #1e40af' }}
            >
                <Modal.Title className="fs-6 fw-bold d-flex flex-column">
                    <div className="d-flex align-items-center gap-2">
                        <span>🏨 Chat en Vivo Booking.com</span>
                        <Badge bg="light" text="dark" style={{ fontSize: '0.68rem' }}>
                            #{bookingChatTrip?.external_reference || bookingChatTrip?.id}
                        </Badge>
                    </div>
                    <small className="fw-normal opacity-75" style={{ fontSize: '0.75rem' }}>
                        👤 Pasajero: {toTitleCase(bookingChatTrip?.passenger_name || '')}
                    </small>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0 d-flex flex-column" style={{ height: '62vh', backgroundColor: '#f1f5f9' }}>
                <div className="p-3 flex-grow-1 overflow-auto d-flex flex-column gap-2">
                    {bookingChatLoading ? (
                        <div className="text-center my-auto py-4 text-muted">
                            <Spinner animation="border" size="sm" variant="primary" className="mb-2" />
                            <div className="small">Cargando mensajes con el pasajero...</div>
                        </div>
                    ) : bookingChatMessages.length === 0 ? (
                        <div className="text-center my-auto p-3 text-muted small bg-white rounded-3 border shadow-xs">
                            <div className="fs-3 mb-1">💬</div>
                            <div className="fw-bold text-dark mb-1">Chat directo con la App de Booking.com</div>
                            <div>
                                Escribe un mensaje o toca una respuesta rápida abajo para avisarle al pasajero dónde lo esperas.
                            </div>
                        </div>
                    ) : (
                        bookingChatMessages.map((msg, idx) => {
                            const pType = String(msg.participant_type || '').toUpperCase();
                            const isCustomer = msg.sender_role === 'CUSTOMER' || msg.direction === 'inbound' || ['PASSENGER', 'CUSTOMER', 'TRAVELLER', 'TRAVELER'].includes(pType);
                            const msgText = msg.content || msg.message || '';
                            const rawTime = msg.created_at || msg.timestamp;
                            const msgKey = msg.id || idx;
                            const timeLabel = rawTime
                                ? new Date(rawTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                : '';
                            const cleanSenderName = String(msg.sender_name || '').replace(/^(👤|🎧|🚗)\s*/, '').trim();
                            return (
                                <div
                                    key={msgKey}
                                    className={`d-flex ${isCustomer ? 'justify-content-start' : 'justify-content-end'}`}
                                >
                                    <div
                                        className="p-2.5 rounded-3 shadow-xs"
                                        style={{
                                            maxWidth: '82%',
                                            padding: '10px 12px',
                                            backgroundColor: isCustomer ? '#ffffff' : '#003580',
                                            color: isCustomer ? '#0f172a' : '#ffffff',
                                            border: isCustomer ? '1px solid #cbd5e1' : '1px solid #1d4ed8'
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center gap-2 mb-1" style={{ fontSize: '0.68rem', opacity: 0.85 }}>
                                            <span className="fw-bold">
                                                {isCustomer
                                                    ? `👤 ${cleanSenderName || toTitleCase(bookingChatTrip?.passenger_name) || 'Pasajero'}`
                                                    : msg.sender_role === 'DISPATCH'
                                                        ? `🎧 ${cleanSenderName || 'Central EmiGo'}`
                                                        : `🚗 ${cleanSenderName || 'Tú (Conductor)'}`}
                                            </span>
                                            <span>{timeLabel}</span>
                                        </div>
                                        <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.88rem' }}>
                                            {msgText}
                                        </div>
                                        {isCustomer && bookingChatTranslations[msgKey] && (
                                            <div
                                                className="mt-2 p-2 rounded-2"
                                                style={{
                                                    backgroundColor: '#eff6ff',
                                                    borderLeft: '3px solid #2563eb',
                                                    fontSize: '0.82rem',
                                                    color: '#1e3a8a'
                                                }}
                                            >
                                                <div className="fw-bold" style={{ fontSize: '0.68rem' }}>
                                                    🌐 Traducción {bookingChatTranslations[msgKey].sourceLang ? `(${bookingChatTranslations[msgKey].sourceLang})` : ''}:
                                                </div>
                                                <div>{bookingChatTranslations[msgKey].text}</div>
                                            </div>
                                        )}
                                        {isCustomer && msgText && (
                                            <div className="mt-1 text-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 text-decoration-none fw-bold d-inline-flex align-items-center gap-1"
                                                    style={{ fontSize: '0.72rem', color: '#2563eb' }}
                                                    onClick={() => handleTranslateDriverChatMessage(msgKey, msgText)}
                                                    disabled={bookingTranslatingId === msgKey}
                                                >
                                                    <Globe size={12} />
                                                    <span>
                                                        {bookingTranslatingId === msgKey
                                                            ? 'Traduciendo...'
                                                            : bookingChatTranslations[msgKey]
                                                                ? 'Ocultar traducción'
                                                                : 'Traducir mensaje'}
                                                    </span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                    <div ref={bookingChatEndRef} />
                </div>

                <div className="px-2 py-1.5 bg-white border-top d-flex gap-1 overflow-auto" style={{ whiteSpace: 'nowrap' }}>
                    {[
                        "👋 Hola, soy tu conductor asignado.",
                        "🚗 Voy en camino al punto de encuentro.",
                        "📍 Ya estoy en el punto de encuentro esperándote.",
                        "🪧 Te espero con un cartel con tu nombre."
                    ].map((chip, idx) => (
                        <Button
                            key={idx}
                            variant="outline-primary"
                            size="sm"
                            className="rounded-pill py-1 px-2 flex-shrink-0"
                            style={{ fontSize: '0.72rem' }}
                            disabled={bookingChatSending}
                            onClick={() => setBookingChatText(chip)}
                        >
                            {chip}
                        </Button>
                    ))}
                </div>

                <Form onSubmit={(e) => handleSendDriverBookingChat(e)} className="p-2 bg-white border-top d-flex gap-1 align-items-center">
                    <Form.Control
                        type="text"
                        placeholder="Escribe al pasajero (puedes tocar 🌐 para traducir)..."
                        maxLength={350}
                        value={bookingChatText}
                        onChange={(e) => setBookingChatText(e.target.value)}
                        disabled={bookingChatSending || bookingTranslatingDraft}
                        style={{ fontSize: '0.88rem' }}
                    />
                    <Button
                        variant="outline-info"
                        type="button"
                        size="sm"
                        className="px-2 py-2 d-flex align-items-center justify-content-center flex-shrink-0"
                        onClick={handleTranslateDriverDraft}
                        disabled={bookingChatSending || bookingTranslatingDraft || !bookingChatText.trim()}
                        title="Traducir mi mensaje al idioma del pasajero antes de enviar"
                    >
                        {bookingTranslatingDraft ? <Spinner size="sm" animation="border" /> : <Globe size={16} />}
                    </Button>
                    <Button
                        type="submit"
                        size="sm"
                        className="px-3 py-2 fw-bold d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ backgroundColor: '#003580', borderColor: '#003580' }}
                        disabled={bookingChatSending || bookingTranslatingDraft || !bookingChatText.trim()}
                    >
                        {bookingChatSending ? <Spinner size="sm" animation="border" /> : <Send size={16} />}
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );

    // CONTROL DE MAPA (ESTABILIZACIÓN CRÍTICA)
    const [mapInstance, setMapInstance] = useState(null);
    const [isFollowing, setIsFollowing] = useState(true); // Auto-seguir al vehículo por defecto
    const hasInitialCentered = useRef(false);
    
    const refreshInterval = useRef(null);
    const heartbeatInterval = useRef(null);

    // RASTRREADORES DE ESTADO REAL (Para evitar closures viejos en el latido)
    const isOnlineRef = useRef(false);
    const currentPosRef = useRef(null);

    useEffect(() => { isOnlineRef.current = isOnline; }, [isOnline]);
    useEffect(() => { currentPosRef.current = currentPos; }, [currentPos]);

    // Auto-recientra el mapa en la posición actual si el modo 'Follow' está activo
    useEffect(() => {
        if (isFollowing && mapInstance && currentPos) {
            mapInstance.panTo(currentPos);
        }
    }, [currentPos, isFollowing, mapInstance]);

    const formatDate = (dateStr, timezone = 'America/Santiago') => {
        if (!dateStr) return '';
        const cleanStr = (dateStr.length === 10 && dateStr.includes('-')) 
            ? `${dateStr}T00:00:00` 
            : dateStr;
        const date = new Date(cleanStr);
        const tz = timezone || 'America/Santiago';
        try {
            return date.toLocaleDateString('es-CL', { 
                weekday: 'short', 
                day: '2-digit', 
                month: 'short',
                timeZone: tz
            });
        } catch (e) {
            return date.toLocaleDateString('es-CL', { weekday: 'short', day: '2-digit', month: 'short' });
        }
    };

    const formatTime = (dateStr, timezone = 'America/Santiago') => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const tz = timezone || 'America/Santiago';
        try {
            return new Intl.DateTimeFormat('es-CL', {
                timeZone: tz,
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }).format(date);
        } catch (e) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        }
    };

    const getSettleMonth = (dateStr) => {
        if (!dateStr) return -1;
        const parts = dateStr.split('-');
        if (parts.length >= 2) {
            return parseInt(parts[1], 10) - 1; // 0-indexed
        }
        return new Date(dateStr).getMonth();
    };

    const getChileMonth = (dateStr, timezone = 'America/Santiago') => {
        if (!dateStr) return -1;
        try {
            const cleanStr = (dateStr.length === 10 && dateStr.includes('-')) 
                ? `${dateStr}T00:00:00` 
                : dateStr;
            const d = new Date(cleanStr);
            const monthStr = d.toLocaleDateString('en-US', { timeZone: timezone || 'America/Santiago', month: 'numeric' });
            return parseInt(monthStr, 10) - 1;
        } catch (e) {
            return new Date(dateStr).getMonth();
        }
    };

    const getSettleYear = (dateStr) => {
        if (!dateStr) return new Date().getFullYear();
        const parts = dateStr.split('-');
        if (parts.length >= 1) {
            return parseInt(parts[0], 10);
        }
        return new Date(dateStr).getFullYear();
    };

    // Helper para mostrar nombres de pasajeros
    const renderPaxBadge = (trip, customClass = "border") => {
        const passengers = [trip.passenger_name, ...(trip.additional_passengers || []).map(p => p.full_name)].filter(Boolean);
        
        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id={`tooltip-pax-${trip.id}`}>
                        <div className="text-start px-1 py-1">
                            <div className="fw-bold border-bottom mb-1 pb-1 small">Pasajeros:</div>
                            {passengers.map((name, i) => <div key={i} className="small">• {name}</div>)}
                        </div>
                    </Tooltip>
                }
            >
                <Badge bg={customClass.includes('dark') ? 'dark' : 'light'} text={customClass.includes('dark') ? 'white' : 'dark'} className={customClass}>
                    <Users size={12} className="me-1"/> {trip.passenger_count} {trip.passenger_count === 1 ? 'pax' : 'paxs'}
                </Badge>
            </OverlayTrigger>
        );
    };

    // Helper para mostrar la categoría de vehículo solicitada (Ejecutivo, VIP, Luxury, Van, SUV, Standard)
    const renderVehicleCategoryBadge = (trip, isBanner = false) => {
        if (!trip) return null;
        const vtName = trip.requested_vehicle_type?.name || 'Estándar';
        const vtLower = vtName.toLowerCase();

        let bg = 'secondary';
        let text = 'white';
        let icon = '🚗';
        let label = vtName;
        let isVip = false;

        if (vtLower.includes('executive') || vtLower.includes('luxury') || vtLower.includes('vip') || vtLower.includes('ejecutivo') || vtLower.includes('lujo')) {
            bg = 'dark';
            text = 'warning';
            icon = '⭐';
            label = `VIP / ${vtName.toUpperCase()}`;
            isVip = true;
        } else if (vtLower.includes('van') || vtLower.includes('minivan') || vtLower.includes('people_carrier') || vtLower.includes('carrier')) {
            bg = 'info';
            text = 'dark';
            icon = '🚐';
            label = `VAN / ${vtName.toUpperCase()}`;
        } else if (vtLower.includes('bus') || vtLower.includes('minibus') || vtLower.includes('minibús')) {
            bg = 'primary';
            text = 'white';
            icon = '🚌';
            label = `BUS / ${vtName.toUpperCase()}`;
        } else if (vtLower.includes('suv') || vtLower.includes('4x4')) {
            bg = 'warning';
            text = 'dark';
            icon = '🚙';
            label = `SUV / ${vtName.toUpperCase()}`;
        }

        if (isBanner) {
            return (
                <div className={`p-3 rounded-3 mb-3 d-flex align-items-center justify-content-between shadow-sm ${isVip ? 'bg-dark text-warning border border-2 border-warning' : 'bg-light text-dark border'}`} style={{ borderRadius: '12px' }}>
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '1.4rem' }}>{icon}</span>
                        <div>
                            <div className="small text-uppercase fw-bold opacity-75" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>CATEGORÍA DE VEHÍCULO REQUERIDA</div>
                            <div className="fw-bold fs-6" style={{ letterSpacing: '0.3px' }}>{label}</div>
                        </div>
                    </div>
                    {isVip ? (
                        <Badge bg="warning" text="dark" className="px-2 py-1.5 fw-bold border border-dark shadow-xs" style={{ fontSize: '11px' }}>
                            EXIGE AUTO ALTA GAMA
                        </Badge>
                    ) : (
                        <Badge bg={bg} text={text} className="px-2 py-1 fw-semibold" style={{ fontSize: '11px' }}>
                            {vtName.toUpperCase()}
                        </Badge>
                    )}
                </div>
            );
        }

        return (
            <Badge bg={bg} text={text} className={`px-2 py-1 d-inline-flex align-items-center gap-1 border ${isVip ? 'border-warning shadow-xs text-warning' : ''}`} style={{ fontSize: '0.75rem', borderRadius: '6px' }}>
                <span>{icon}</span> <span className="fw-bold">{label}</span>
            </Badge>
        );
    };

    // Cargar Liquidaciones
    const loadSettlements = useCallback(async () => {
        setLoadingSettlements(true);
        try {
            const res = await apiClient.get('/transport/drivers/settlements/me');
            setSettlementsData(res.data);
        } catch (error) {
            console.error("Error cargando liquidaciones:", error);
            toast.error("Error al cargar pagos.");
        } finally {
            setLoadingSettlements(false);
        }
    }, []);

    const [isUploadingInvoice, setIsUploadingInvoice] = useState(null);
    const [isUploadingMonthlyInvoice, setIsUploadingMonthlyInvoice] = useState(false);

    const handleUploadInvoice = async (settlementId, fileOrBlob, filename = 'invoice.jpg') => {
        setIsUploadingInvoice(settlementId);
        try {
            const formData = new FormData();
            formData.append('file', fileOrBlob, filename);
            const res = await apiClient.post(`/transport/settlements/${settlementId}/invoice`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(`📸 Factura procesada! Folio detectado: ${res.data.invoice_number || 'No detectado'}`);
            loadSettlements();
        } catch (error) {
            console.error("Error al subir factura:", error);
            toast.error(error.response?.data?.detail || "Error al subir y procesar la factura.");
        } finally {
            setIsUploadingInvoice(null);
        }
    };

    const handleUploadMonthlyInvoice = async (year, month, fileOrBlob, filename = 'monthly_invoice.jpg') => {
        setIsUploadingMonthlyInvoice(true);
        try {
            const res = await uploadMonthlySettlementInvoice(year, month, fileOrBlob, filename);
            toast.success(`📸 Factura mensual vinculada a ${res.settlements_count || ''} liquidación(es)! Folio: ${res.invoice_number || 'Detectado'}`);
            loadSettlements();
        } catch (error) {
            console.error("Error al subir factura mensual:", error);
            toast.error(error.response?.data?.detail || "Error al subir y procesar la factura mensual.");
        } finally {
            setIsUploadingMonthlyInvoice(false);
        }
    };

    const renderInvoiceSection = (s) => {
        if (s.invoice_number) {
            return (
                <div className="mt-2 text-start bg-success bg-opacity-10 p-2 rounded border border-success border-opacity-25 animate-fade-in">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
                        <span className="small text-success-emphasis fw-bold">📄 Factura Folio: #{s.invoice_number}</span>
                        {s.invoice_url && (
                            <Button 
                                variant="link" 
                                size="sm" 
                                className="p-0 text-decoration-none fw-bold text-success small" 
                                style={{fontSize: '11px'}}
                                onClick={() => window.open(s.invoice_url, '_blank')}
                            >
                                Ver Factura ↗
                            </Button>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div className="mt-2 text-start animate-fade-in">
                {isUploadingInvoice === s.id ? (
                    <div className="text-center text-muted small py-1 bg-light rounded border border-dashed" style={{fontSize: '11px'}}>
                        <Spinner animation="border" size="sm" className="me-2 text-primary" style={{width:'12px', height:'12px'}} />
                        Procesando documento con Gemini OCR...
                    </div>
                ) : (
                    <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center">
                        <span className="small text-muted me-auto" style={{fontSize:'11px'}}>Cargar Factura (SII):</span>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="py-1 px-2 fw-bold d-flex align-items-center gap-1"
                            style={{fontSize: '10px'}}
                            onClick={async () => {
                                if (Capacitor.isNativePlatform()) {
                                    try {
                                        const image = await CapCamera.getPhoto({
                                            quality: 90,
                                            allowEditing: false,
                                            resultType: CameraResultType.Base64,
                                            source: CameraSource.Camera,
                                            promptLabelHeader: 'Fotografiar Factura'
                                        });
                                        if (image) {
                                            const base64Response = await fetch(`data:image/jpeg;base64,${image.base64String}`);
                                            const blob = await base64Response.blob();
                                            handleUploadInvoice(s.id, blob, "invoice_photo.jpg");
                                        }
                                    } catch (err) {
                                        if (err.message !== 'User cancelled photos app' && err.message !== 'Unable to get photo') {
                                            console.error(err);
                                            toast.error("Error al abrir cámara");
                                        }
                                    }
                                } else {
                                    const cameraInput = document.getElementById(`invoice-camera-input-${s.id}`);
                                    if (cameraInput) cameraInput.click();
                                }
                            }}
                        >
                            📸 Cámara
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="py-1 px-2 fw-bold d-flex align-items-center gap-1"
                            style={{fontSize: '10px'}}
                            onClick={() => {
                                const fileInput = document.getElementById(`invoice-file-input-${s.id}`);
                                if (fileInput) fileInput.click();
                            }}
                        >
                            📁 Archivo (PDF/Img)
                        </Button>
                        <input
                            type="file"
                            id={`invoice-file-input-${s.id}`}
                            accept="image/*,application/pdf"
                            style={{display: 'none'}}
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    handleUploadInvoice(s.id, file, file.name);
                                }
                            }}
                        />
                        {!Capacitor.isNativePlatform() && (
                            <input
                                type="file"
                                id={`invoice-camera-input-${s.id}`}
                                accept="image/*"
                                capture="environment"
                                style={{display: 'none'}}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        handleUploadInvoice(s.id, file, file.name);
                                    }
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    // 1. Cargar Datos Iniciales y Gestionar Notificaciones
    const loadData = useCallback(async (isBackground = false) => {
        console.log("🔄 [PORTAL] Iniciando carga de datos...", { userEmail: user?.email, driverId: user?.linked_driver_id });
        
        if (!user?.linked_driver_id) {
            console.warn("⚠️ [PORTAL] Usuario no tiene driver_id vinculado.");
            setLoading(false);
            return;
        }

        try {
            // SEGURIDAD: Timeout de 10 segundos para la carga de datos
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error("Timeout en conexión con servidor")), 12000)
            );

            const [tripsRes, driversRes] = await Promise.race([
                Promise.allSettled([
                    apiClient.get('/transport/trips/', { 
                        params: { 
                            driver_id: user.linked_driver_id, 
                            limit: 2000,
                            // Mostrar viajes desde el inicio del mes actual o 7 días atrás (lo que sea menor)
                            start_date: (() => {
                                const now = new Date();
                                const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
                                const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                                const startDate = new Date(Math.min(startOfCurrentMonth.getTime(), sevenDaysAgo.getTime()));
                                return startDate.toISOString();
                            })()
                        } 
                    }),
                    getDrivers()
                ]),
                timeoutPromise
            ]);
            
            const currentDriver = (driversRes?.status === 'fulfilled' && Array.isArray(driversRes.value)) 
                ? driversRes.value.find(d => d.id === user.linked_driver_id) 
                : null;
            if (currentDriver) {
                setDriverInfo(currentDriver);
            }
            if (tripsRes?.status === 'fulfilled' && tripsRes.value?.data) {
                setTrips(tripsRes.value.data);
            }

            // Cargar estado de confirmación de agenda para mañana
            try {
                const ackRes = await apiClient.get('/transport/drivers/notifications/tomorrow-acknowledgment');
                setTomorrowAck({
                    acknowledged: ackRes.data.acknowledged,
                    acknowledged_at: ackRes.data.acknowledged_at,
                    was_sent: ackRes.data.was_sent,
                    target_date: ackRes.data.target_date
                });
            } catch (ackErr) {
                console.error("⚠️ Error cargando confirmación de agenda:", ackErr);
            }

            // Cargar conteo de mensajes no leídos del Live Chat de Booking.com
            try {
                const unreadBookingRes = await getDriverUnreadBookingChats(user.linked_driver_id);
                const unreadMap = unreadBookingRes?.trips_unread || unreadBookingRes?.unread_by_trip;
                if (unreadMap) {
                    setBookingUnreadMap(unreadMap);
                }
            } catch (chatErr) {
                console.warn("⚠️ Error cargando chats no leídos de Booking:", chatErr);
            }
            
            console.log(" [PORTAL] Datos cargados con éxito.");

            if (currentDriver) {
                setIsOnline(currentDriver.is_online);
                // Si NO tiene un vehículo código asignado, forzar selección
                if (!currentDriver.assigned_vehicle_code) {
                    setShowVehicleSelection(true);
                } else {
                    setShowVehicleSelection(false);
                }
            }
            setLoadingError(null);
        } catch (error) {
            console.error("❌ [PORTAL] Error cargando datos:", error);
            setLoadingError(error.message || "Error de conexión");
        } finally {
            if (!isBackground) setLoading(false);
        }
    }, [user]);

    // EFECTO PARA CARGAR HISTORIAL DE MESES BAJO DEMANDA
    useEffect(() => {
        if (!user?.linked_driver_id) return;
        if (historyMonth === undefined || historyMonth === null) return;
        if (fetchedMonths.has(historyMonth)) return;

        const fetchMonthHistory = async () => {
            setLoadingHistory(true);
            try {
                let startDateStr = '';
                let endDateStr = '';
                
                if (historyMonth === -1) {
                    startDateStr = '2024-01-01T00:00:00-04:00';
                    endDateStr = '2030-12-31T23:59:59-04:00';
                } else {
                    const now = new Date();
                    let year = now.getFullYear();
                    if (historyMonth > now.getMonth()) {
                        year -= 1;
                    }
                    const lastDay = new Date(year, historyMonth + 1, 0).getDate();
                    const mStr = String(historyMonth + 1).padStart(2, '0');
                    const lastDayStr = String(lastDay).padStart(2, '0');
                    startDateStr = `${year}-${mStr}-01T00:00:00-04:00`;
                    endDateStr = `${year}-${mStr}-${lastDayStr}T23:59:59-04:00`;
                }

                const res = await apiClient.get('/transport/trips/', {
                    params: {
                        driver_id: user.linked_driver_id,
                        start_date: startDateStr,
                        end_date: endDateStr,
                        limit: 2000
                    }
                });

                if (res.data && Array.isArray(res.data)) {
                    setTrips(prev => {
                        const existingIds = new Set(prev.map(t => t.id));
                        const newItems = res.data.filter(t => !existingIds.has(t.id));
                        return [...prev, ...newItems];
                    });
                }
                setFetchedMonths(prev => new Set(prev).add(historyMonth));
            } catch (err) {
                console.error("❌ Error cargando historial del mes:", err);
            } finally {
                setLoadingHistory(false);
            }
        };

        fetchMonthHistory();
    }, [historyMonth, user?.linked_driver_id, fetchedMonths]);

    // EFECTO PARA NOTIFICACIONES PUSH
    useEffect(() => {
        const setupNotifications = async () => {
            console.log("🕵️ REVISANDO ESTADO PARA PUSH. User:", user?.name, "DriverID:", user?.linked_driver_id);
            
            if (user?.linked_driver_id) {
                console.log("📡 Intentando obtener Token para el chofer...");
                const token = await requestForToken();
                
                if (token) {
                    setPushEnabled(true);
                    console.log("🔥 TOKEN FCM DISPOSITIVO:", token); 
                    try {
                        await apiClient.post('/transport/drivers/fcm-token', { fcm_token: token });
                        console.log(" Token guardado en servidor.");
                    } catch (e) {
                        console.error("❌ Error guardando token FCM:", e);
                    }
                } else {
                    setPushEnabled(false);
                    // Si los permisos están bloqueados y estamos en móvil, el PermissionsCheckModal se encargará
                }
            }
        };

        setupNotifications();

        // Verificar si hay alguna acción de notificación push pendiente al arrancar la app
        const pendingAction = getPendingPushAction();
        if (pendingAction && pendingAction.data && pendingAction.data.type === 'confirm_board') {
            console.log("✈️ CARGANDO ACCIÓN PUSH PENDIENTE AL ARRANCAR:", pendingAction);
            setConfirmBoardTrip({
                tripId: parseInt(pendingAction.data.trip_id),
                firstMovementAt: pendingAction.data.first_movement_at
            });
        }

        // Conectar el listener híbrido para mensajes en vivo
        const startListening = () => {
            onMessageListener().then(payload => {
                if (payload) {
                    console.log("🔔 NOTIFICACIÓN RECIBIDA:", payload);
                    const data = payload.data || {};
                    
                    if (data.type === 'confirm_board') {
                        setConfirmBoardTrip({
                            tripId: parseInt(data.trip_id),
                            firstMovementAt: data.first_movement_at
                        });
                    } else {
                        setInAppAlert({
                            title: payload.notification?.title || "Nueva Notificación",
                            body: payload.notification?.body || ""
                        });
                        toast.info(`🔔 ${payload.notification?.title}: ${payload.notification?.body}`, {
                            position: "top-center",
                            autoClose: 5000
                        });
                    }
                    loadData(true); // Recargar viajes automáticamente
                }
                startListening(); // Re-activar escucha para el siguiente mensaje
            }).catch(err => console.log('Push listener error: ', err));
        };

        startListening();

        return () => {
            // Capacitor maneja la limpieza internamente o vía removeAllListeners en firebase.js
        };
    }, [user, loadData]);

    // 2. Lógica de Heartbeat (Señal de Vida) - BLINDADA PARA ESTABILIDAD
    const sendHeartbeat = useCallback(async () => {
        if (!user?.linked_driver_id) return;
        
        const onlineStatus = isOnlineRef.current;
        const position = currentPosRef.current;

        console.log("💓 [HEARTBEAT] Enviando pulso...", { online: onlineStatus, pos: position });

        try {
            apiClient.post('/transport/drivers/heartbeat', null, {
                params: {
                    driver_id: user.linked_driver_id,
                    is_online: onlineStatus,
                    lat: position?.lat,
                    lng: position?.lng
                }
            }).catch(e => console.warn("Hipo heartbeat:", e.message));
        } catch (error) {}
    }, [user?.linked_driver_id]); // Solo depende del ID del usuario, no de la posición ni el estado online

    useEffect(() => {
        loadData();
        
        // --- FLUJO DE PERMISOS NATIVOS REFORZADO ---
        if (NativeBridge.isNative) {
            const checkPermissions = async () => {
                try {
                    const status = await NativeBridge.checkPermissions();
                    console.log("🛠️ ESTADO PERMISOS ACTUAL:", status);
                    
                    // Solo el GPS es bloqueante para el funcionamiento básico del rastreo.
                    // Las notificaciones push no deben bloquear la app si fallan o se deniegan.
                    if (status.gps !== 'granted') {
                        setShowLocationDisclosure(true);
                        setShowPermissionsModal(true);
                    }
                } catch (e) {
                    console.warn("Error al revisar permisos nativos:", e);
                }
            };
            checkPermissions();
        }

        // Monitor de posición constante (Radar GPS)
        let watchIdValue = null;
        const startWatching = async () => {
            watchIdValue = await NativeBridge.watchPosition(
                { 
                    enableHighAccuracy: true, 
                    timeout: 5000,
                    maximumAge: 0
                },
                (pos, err) => {
                    if (pos) {
                        const newPos = { lat: pos.lat, lng: pos.lng };
                        setCurrentPos(newPos);
                        console.log("📍 [RADAR] Nueva posición detectada:", newPos);
                    } else if (err) {
                        console.error("GPS Error:", err);
                    }
                }
            );
        };
        startWatching();

        // Intervalos de largo plazo (No dependen de cambios de estado frecuentes)
        refreshInterval.current = setInterval(() => loadData(true), 60000);
        heartbeatInterval.current = setInterval(sendHeartbeat, 45000);

        return () => {
            clearInterval(refreshInterval.current);
            clearInterval(heartbeatInterval.current);
            if (watchIdValue) {
                NativeBridge.clearWatch(watchIdValue);
            }
        };
    }, [user?.linked_driver_id, loadData, sendHeartbeat]); // Incluimos las funciones como dependencias

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log("📱 App visible/resumed. Re-checking permissions and status...");
                // 1. Re-check GPS permissions
                if (NativeBridge.isNative) {
                    const checkPermissions = async () => {
                        try {
                            const status = await NativeBridge.checkPermissions();
                            console.log("🛠️ ESTADO PERMISOS ACTUAL (RESUME):", status);
                            if (status.gps !== 'granted') {
                                setShowPermissionsModal(true);
                            } else {
                                setShowPermissionsModal(false);
                            }
                        } catch (e) {
                            console.warn("Error check permissions on visibilitychange:", e);
                        }
                    };
                    checkPermissions();
                }
                // 2. Re-check approval / load updated driver status and trips
                loadData();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [loadData]);

    const handleActivatePermissions = async () => {
        try {
            await NativeBridge.requestPermissions();
            
            // Verificamos el estado final del GPS para decidir si quitamos el modal
            const currentStatus = await NativeBridge.checkPermissions();
            if (currentStatus.gps === 'granted') {
                toast.success("GPS activado correctamente.");
                setShowPermissionsModal(false);
                
                // Intentamos registrar las notificaciones push en segundo plano de forma no bloqueante
                requestForToken().then(token => {
                    if (token) setPushEnabled(true);
                }).catch(e => console.warn("Error push no bloqueante:", e));
            } else {
                toast.error("⚠️ El permiso de GPS (Ubicación) es obligatorio para poder operar.");
            }
        } catch (error) {
            console.error("Error activando permisos:", error);
            toast.error("Error al solicitar permisos.");
            
            // Salvaguarda: si a pesar del error de la API nativa (p.ej. falló push en iOS),
            // el GPS ya está concedido, dejamos entrar al conductor.
            try {
                const currentStatus = await NativeBridge.checkPermissions();
                if (currentStatus.gps === 'granted') {
                    setShowPermissionsModal(false);
                }
            } catch (e) {}
        }
    };

    const handleAssignVehicle = async (vehicleId) => {
        setIsAssigningVehicle(true);
        try {
            await apiClient.post('/transport/drivers/assign-vehicle', { vehicle_id: vehicleId });
            
            // Una vez asignado, lo ponemos ONLINE de inmediato
            await apiClient.post('/transport/drivers/heartbeat', null, {
                params: {
                    driver_id: user.linked_driver_id,
                    is_online: true,
                    lat: currentPos?.lat,
                    lng: currentPos?.lng
                }
            });
            
            setIsOnline(true);
            toast.success("Turno iniciado. ¡Buen viaje!");
            await loadData(); 
            setShowVehicleSelection(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al iniciar turno");
        } finally {
            setIsAssigningVehicle(false);
        }
    };

    const handleReleaseVehicle = async () => {
        if (isOnline) {
            alert("Primero debes finalizar el turno (OFFLINE) para poder cambiar o liberar el móvil.");
            return;
        }
        if (!window.confirm("¿Confirmas que dejas este móvil? Quedará disponible para otros compañeros.")) return;
        try {
            await apiClient.post('/transport/drivers/release-vehicle');
            toast.info("Móvil liberado.");
            await loadData(); 
        } catch (e) {
            toast.error("Error al liberar móvil.");
        }
    };

    const toggleOnlineStatus = async () => {
        const newStatus = !isOnline;
        setIsOnline(newStatus);
        toast.info(newStatus ? " Estás disponible para viajes" : "⏸️ Estás en pausa (No Disponible)");
        
        try {
            await apiClient.post('/transport/drivers/heartbeat', null, {
                params: {
                    driver_id: user.linked_driver_id,
                    is_online: newStatus,
                    lat: currentPos?.lat,
                    lng: currentPos?.lng
                }
            });
        } catch (e) {
            console.error("Error toggling online status", e);
        }
    };

    const handleLogout = async () => {
        if (!window.confirm("Estas a punto de cerrar sesion, con esto no recibiras reservas.")) {
            return;
        }
        if (isOnline && user?.linked_driver_id) {
            try {
                await apiClient.post('/transport/drivers/heartbeat', null, {
                    params: {
                        driver_id: user.linked_driver_id,
                        is_online: false,
                        lat: currentPos?.lat,
                        lng: currentPos?.lng
                    }
                });
            } catch (e) {
                console.error("Error al pasar offline durante logout:", e);
            }
        }
        logout();
    };

    // 3. Botón de Pánico (🚨)
    const handlePanic = async () => {
        if (!currentPos) {
            alert("No se puede enviar alerta sin ubicación GPS activa.");
            return;
        }
        if (!window.confirm("¿ESTÁS EN PELIGRO? Esta alerta avisará de inmediato a la central.")) return;

        setIsSendingPanic(true);
        try {
            await apiClient.post('/transport/panic-alert', {
                lat: currentPos.lat,
                lng: currentPos.lng,
                driver_id: user.linked_driver_id,
                trip_id: selectedTrip?.id || null
            });
            toast.error("🚨 ALERTA DE PÁNICO ENVIADA.", {
                position: "top-center",
                autoClose: false
            });
        } catch (error) {
            alert("Error al enviar alerta de pánico. Por favor llama al 133.");
        } finally {
            setIsSendingPanic(false);
        }
    };


    // 4. Lógica de EMI-GO LENS (CÁMARA DE EVIDENCIA)
    const handleCaptureEvidence = async (markerType = 'evidence') => {
        if (!currentPos) {
            toast.error("Necesitamos ubicación GPS para la evidencia.");
            return;
        }

        try {
            const image = await CapCamera.getPhoto({
                quality: 90,
                allowEditing: false,
                resultType: CameraResultType.Base64,
                source: CameraSource.Prompt, // Permite elegir galería para pantallazos
                promptLabelHeader: 'EmiGo Lens',
                promptLabelPhoto: 'Elegir de Galería (Pantallazos)',
                promptLabelPicture: 'Tomar Fotografía ahora'
            });

            if (image) {
                setIsCapturingEvidence(true);
                
                // Convertir Base64 a Blob
                const base64Response = await fetch(`data:image/jpeg;base64,${image.base64String}`);
                const blob = await base64Response.blob();
                
                const formData = new FormData();
                formData.append('file', blob, `lens_${selectedTrip.id}.jpg`);
                formData.append('lat', currentPos.lat);
                formData.append('lng', currentPos.lng);
                formData.append('marker_type', markerType);

                await apiClient.post(`/transport/trips/${selectedTrip.id}/evidence`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                toast.success("📸 Evidencia guardada con marca de agua GPS.");
                return true;
            }
        } catch (error) {
            if (error.message !== 'User cancelled photos app') {
                console.error("Error Lens:", error);
                toast.error("Error al capturar evidencia.");
            }
        } finally {
            setIsCapturingEvidence(false);
        }
    };

    // 5. Lógica GPS, Tracking y WAKE LOCK
    const watchId = useRef(null);
    const lastActiveTripId = useRef(null);
    const wakeLock = useRef(null);
    const lastSendTime = useRef(0);
    const lastSentCoords = useRef(null);

    const requestWakeLock = useCallback(async () => {
        if ('wakeLock' in navigator) {
            try {
                if (!wakeLock.current) {
                    wakeLock.current = await navigator.wakeLock.request('screen');
                    console.log("🕯️ WakeLock Activado");
                }
            } catch (err) { console.warn('Wake Lock no disponible'); }
        }
    }, []);

    const releaseWakeLock = useCallback(() => {
        if (wakeLock.current) {
            wakeLock.current.release();
            wakeLock.current = null;
        }
    }, []);

    useEffect(() => {
        requestWakeLock();
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestWakeLock();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            releaseWakeLock();
        };
    }, [requestWakeLock, releaseWakeLock]);

    const activeTripRef = useRef(null);
    const gpsIntervalRef = useRef(null); // ⏱️ Timer de respaldo para iOS segundo plano

    // TRACKING GPS (REFACTORIZADO PARA SEGUNDO PLANO CON CAPACITOR Y FALLBACK WEB)
    const activeTrip = useMemo(() => {
        return trips.find(t => ['en_route', 'arrived', 'on_board'].includes(t.status.toLowerCase()));
    }, [trips]);
    const activeTripId = activeTrip?.id || null;
    const activeTripStatus = activeTrip?.status || null;

    useEffect(() => {
        // Sincronizar referencia para que el callback siempre tenga el ID actual
        activeTripRef.current = activeTrip;

        // Función central de envío, usada tanto por el callback del plugin como por el timer de respaldo
        const sendPositionIfDue = (latitude, longitude, speed, heading, locationTime, forceHeartbeat = false) => {
            const currentTrip = activeTripRef.current;
            const onlineStatus = isOnlineRef.current;

            // Intervalo de envío: 90s en viaje, 4 min disponible
            const sendInterval = currentTrip ? 90000 : 240000;
            const now = locationTime || Date.now();

            const shouldSend = forceHeartbeat
                || lastSendTime.current === 0
                || !lastSentCoords.current
                || (now - lastSendTime.current >= sendInterval);

            if (!shouldSend) return;

            if (currentTrip) {
                lastSendTime.current = now;
                lastSentCoords.current = { lat: latitude, lng: longitude };

                apiClient.post(`/transport/trips/${currentTrip.id}/track`, {
                    trip_id: currentTrip.id,
                    lat: latitude,
                    lng: longitude,
                    speed: speed || 0,
                    heading: heading || 0,
                    timestamp: new Date(now).toISOString()
                }).then(() => {
                    console.log(`📍 GPS #${currentTrip.id} - Punto enviado (${Math.round(speed || 0)} km/h)`);
                }).catch(err => {
                    console.warn("Hipo de red en GPS:", err.message);
                });
            } else if (onlineStatus && user?.linked_driver_id) {
                lastSendTime.current = now;
                lastSentCoords.current = { lat: latitude, lng: longitude };

                apiClient.post('/transport/drivers/heartbeat', null, {
                    params: {
                        driver_id: user.linked_driver_id,
                        is_online: true,
                        lat: latitude,
                        lng: longitude
                    }
                }).then(() => {
                    console.log("💓 [HEARTBEAT BG] Pulso de fondo enviado");
                }).catch(e => console.warn("Hipo heartbeat BG:", e.message));
            }
        };

        const handleLocationUpdate = (coords) => {
            const { latitude, longitude, speed, heading, time } = coords;
            const newCoords = { lat: latitude, lng: longitude };
            setCurrentPos(newCoords);

            if (!hasInitialCentered.current && mapInstance) {
                mapInstance.panTo(newCoords);
                hasInitialCentered.current = true;
            }

            // Guardar siempre la última posición conocida para el timer de respaldo
            lastSentCoords.current = lastSentCoords.current || newCoords;
            // Actualizamos coords conocidas sin marcarlas como enviadas todavía
            if (!lastSentCoords._raw) lastSentCoords._raw = {};
            lastSentCoords._raw = { latitude, longitude, speed, heading };

            const locationTime = time || Date.now();
            sendPositionIfDue(latitude, longitude, speed, heading, locationTime);
        };

        // ⏱️ Timer de respaldo: dispara cada 90s y verifica si toca enviar
        // Garantiza envíos aunque iOS no entregue callbacks del plugin puntualmente
        const startFallbackTimer = () => {
            if (gpsIntervalRef.current) clearInterval(gpsIntervalRef.current);
            gpsIntervalRef.current = setInterval(() => {
                let raw = lastSentCoords._raw;
                if (!raw) {
                    // Fallback: usar la última posición del radar GPS si el watcher en segundo plano no se ha disparado (p. ej. si está quieto)
                    const fallbackPos = currentPosRef.current;
                    if (fallbackPos && fallbackPos.lat && fallbackPos.lng) {
                        raw = {
                            latitude: fallbackPos.lat,
                            longitude: fallbackPos.lng,
                            speed: 0,
                            heading: 0
                        };
                    }
                }
                if (!raw) return; // Aún no tenemos ninguna posición conocida
                const { latitude, longitude, speed, heading } = raw;
                console.log("⏱️ [GPS TIMER] Verificando si toca enviar...");
                sendPositionIfDue(latitude, longitude, speed, heading, Date.now());
            }, 90000); // Verificar cada 90 segundos
        };

        const startTracking = async () => {
            const onlineStatus = isOnlineRef.current;
            const currentActiveTrip = activeTripRef.current;
            if (!currentActiveTrip && !onlineStatus) {
                if (watchId.current) {
                    console.log("⛔ Deteniendo rastreo GPS");
                    if (Capacitor.isNativePlatform()) {
                        BackgroundGeolocation.stop().catch(e => {
                            console.warn("Error deteniendo watcher nativo:", e);
                        });
                    } else if (navigator.geolocation) {
                        try { navigator.geolocation.clearWatch(watchId.current); } catch(err) {}
                    }
                    watchId.current = null;
                    lastSentCoords.current = null;
                    lastSendTime.current = 0;
                    lastActiveTripId.current = null;
                }
                if (gpsIntervalRef.current) {
                    clearInterval(gpsIntervalRef.current);
                    gpsIntervalRef.current = null;
                }
                return;
            }

            if (Capacitor.isNativePlatform()) {
                const currentTripId = currentActiveTrip ? currentActiveTrip.id : 'no_trip';
                if (watchId.current && lastActiveTripId.current !== currentTripId) {
                    BackgroundGeolocation.stop().catch(e => {
                        console.warn("Error deteniendo watcher nativo:", e);
                    });
                    watchId.current = null;
                    lastSentCoords.current = null;
                    lastSendTime.current = 0;
                }

                if (!watchId.current) {
                    console.log("🛰️ Iniciando rastreo GPS (Background) para:", currentActiveTrip ? `Viaje ${currentActiveTrip.id}` : "Disponible");
                    lastActiveTripId.current = currentTripId;

                    try {
                        await BackgroundGeolocation.start(
                            {
                                backgroundMessage: currentActiveTrip ? "Rastreando ubicación del viaje." : "Móvil disponible para recibir servicios.",
                                backgroundTitle: currentActiveTrip ? "EmiGo - En Viaje" : "EmiGo - Disponible",
                                requestPermissions: true,
                                stale: false,
                                distanceFilter: 0  // Sin filtro de distancia → iOS emite periódicamente aunque estés quieto
                            },
                            function (location, error) {
                                if (error) {
                                    console.error("Error capturando GPS nativo:", error);
                                    return;
                                }
                                handleLocationUpdate(location);
                            }
                        );
                        watchId.current = true;
                    } catch (err) {
                        console.error("Error crítico en BackgroundGeolocation.start:", err);
                        console.log("⚠️ Reintentando con Geolocation estándar (HTML5)...");
                        try {
                            watchId.current = navigator.geolocation.watchPosition(
                                (pos) => { handleLocationUpdate(pos.coords); },
                                (err) => console.error("Error capturando GPS Web Fallback:", err),
                                { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
                            );
                        } catch (webErr) {
                            console.error("Fallo definitivo en Geolocation estándar:", webErr);
                        }
                    }
                }

                // Iniciar el timer de respaldo para iOS/Android segundo plano
                startFallbackTimer();

            } else if (navigator.geolocation) {
                const currentTripId = currentActiveTrip ? currentActiveTrip.id : 'no_trip';
                if (watchId.current && lastActiveTripId.current !== currentTripId) {
                    navigator.geolocation.clearWatch(watchId.current);
                    watchId.current = null;
                    lastSentCoords.current = null;
                    lastSendTime.current = 0;
                }

                if (!watchId.current) {
                    console.log("🌐 Iniciando rastreo GPS (Web) para:", currentActiveTrip ? `Viaje ${currentActiveTrip.id}` : "Disponible");
                    lastActiveTripId.current = currentTripId;

                    watchId.current = navigator.geolocation.watchPosition(
                        (pos) => { handleLocationUpdate(pos.coords); },
                        (err) => console.error("Error capturando GPS:", err),
                        { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
                    );
                }

                // Iniciar el timer de respaldo
                startFallbackTimer();
            }
        };

        startTracking();

        // Cleanup: detener el timer de respaldo cuando el efecto se destruya
        return () => {
            if (gpsIntervalRef.current) {
                clearInterval(gpsIntervalRef.current);
                gpsIntervalRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTripId, activeTripStatus, mapInstance, isOnline, user?.linked_driver_id]);



    // --- LÓGICA DE ALERTAS PREVENTIVAS (40 MINUTOS) ---
    const getCriticalTrips = useCallback(() => {
        const now = new Date();
        const threshold = 40 * 60000; // 40 minutos en ms

        return trips.filter(trip => {
            const tripTime = new Date(trip.scheduled_time);
            const diff = tripTime - now;
            const status = trip.status.toLowerCase();

            // Caso 1: Falta poco y NO HA ACEPTADO (assigned)
            if (status === 'assigned' && diff > 0 && diff < threshold) return true;
            // Caso 2: Falta poco, ACEPTÓ pero NO HA INICIADO (confirmed)
            if (status === 'confirmed' && diff > 0 && diff < threshold) return true;

            return false;
        });
    }, [trips]);

    const criticalTrips = getCriticalTrips();

    // --- LÓGICA DE OFERTA ASAP (OVERLAY) ---
    const asapOffer = useMemo(() => {
        return trips.find(t => t.is_asap && t.asap_status === 'offered' && t.driver_id === user.linked_driver_id);
    }, [trips, user.linked_driver_id]);

    const [offerCountdown, setOfferCountdown] = useState(30);
    
    useEffect(() => {
        let timer;
        if (asapOffer) {
            // Calcular segundos restantes basados en la expiración del servidor
            const expiresAt = new Date(asapOffer.current_offer_expires_at).getTime();
            const now = new Date().getTime();
            const diff = Math.max(0, Math.floor((expiresAt - now) / 1000));
            setOfferCountdown(diff);

            if (diff <= 0) {
                // Oferta ya expirada en servidor, refrescar inmediatamente
                loadData(true);
                return;
            }

            timer = setInterval(() => {
                setOfferCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        // Cerrar oferta expirada localmente y refrescar en segundo plano
                        setTimeout(() => loadData(true), 1500);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            
            // Sonido de alerta ASAP
            const playAlert = () => {
                const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3');
                audio.play().catch(e => console.log("Bloqueo de audio"));
            };
            playAlert();
        }
        return () => clearInterval(timer);
    }, [asapOffer, loadData]);

    const handleSelectTrip = (trip) => { 
        setDirections(null); 
        setDriverComment(""); 
        setSelectedTrip(trip); 
        hasInitialCentered.current = false;

        const statusLower = trip.status?.toLowerCase();
        if (['assigned', 'pending'].includes(statusLower)) {
            markTripAsSeen(trip.id, 'new');
        } else if (['confirmed', 'en_route', 'arrived', 'on_board'].includes(statusLower)) {
            markTripAsSeen(trip.id, 'agenda');
        }
    };

    const directionsCallback = useCallback((response) => { 
        if (response !== null && response.status === 'OK' && !directions) setDirections(response); 
    }, [directions]);

    const handleAction = async (tripId, newStatus, extra = {}) => {
        // Bloquear intentos de regresión de estado en viajes ya finalizados/cancelados
        const currentTripObj = trips.find(t => t.id === tripId) || selectedTrip;
        const currentStatusLower = (currentTripObj?.status || '').toLowerCase();
        if (['completed', 'cancelled', 'no_show', 'anulado'].includes(currentStatusLower) && ['en_route', 'arrived', 'on_board', 'assigned', 'pending'].includes(newStatus)) {
            console.warn(`[BLOCKED ACTION] Intento de cambiar estado del viaje #${tripId} de '${currentStatusLower}' a '${newStatus}' abortado.`);
            return;
        }

        // Restricción de inicio de ruta (hasta 2 horas antes del servicio)
        if (newStatus === 'en_route' && currentTripObj?.scheduled_time) {
            if (!isTripAllowedForEnRoute(currentTripObj.scheduled_time)) {
                alert("No es posible iniciar el trayecto todavía. El botón se habilitará 2 horas antes del servicio.");
                return;
            }
        }

        if (newStatus === 'on_board' && !window.confirm("¿Está seguro que el pasajero ya subió a bordo?")) return;
        setIsSubmittingAction(true);
        try {
            const updateData = { status: newStatus, ...extra };
            if (currentPos && Number.isFinite(currentPos.lat)) {
                if (newStatus === 'en_route') { updateData.gps_en_route_lat = currentPos.lat; updateData.gps_en_route_lng = currentPos.lng; }
                else if (newStatus === 'arrived') { updateData.gps_arrived_lat = currentPos.lat; updateData.gps_arrived_lng = currentPos.lng; }
                else if (newStatus === 'on_board') { updateData.gps_on_board_lat = currentPos.lat; updateData.gps_on_board_lng = currentPos.lng; }
                else if (newStatus === 'completed') { updateData.gps_completed_lat = currentPos.lat; updateData.gps_completed_lng = currentPos.lng; }
            }
            if (newStatus === 'completed' && driverComment) updateData.driver_notes = driverComment;
            const updatedTripData = await updateTrip(tripId, updateData);
            await loadData(true);

            // Enviar pulso de GPS inmediato para el caminito
            if (currentPos && Number.isFinite(currentPos.lat)) {
                try {
                    await apiClient.post(`/transport/trips/${tripId}/track`, { 
                        trip_id: tripId, 
                        lat: currentPos.lat, 
                        lng: currentPos.lng, 
                        speed: 0, 
                        heading: 0,
                        timestamp: new Date().toISOString()
                    });
                    console.log(` GPS #${tripId} - Pulso inmediato por cambio de estado`);
                } catch (gpsError) {
                    console.error("Error al enviar pulso GPS inmediato:", gpsError);
                }
            }

            if (newStatus === 'pending') setSelectedTrip(null);
            else if (selectedTrip) setSelectedTrip(updatedTripData);
        } catch (error) { 
            alert(error.response?.data?.detail || "Error al actualizar servicio"); 
        } finally {
            setIsSubmittingAction(false);
        }
    };

    const handleNoShow = async () => {
        if (!window.confirm("¿Confirmas que el pasajero no se presentó?")) return;
        
        let uploadCount = 0;
        let continueUploading = true;
        
        if (window.confirm("¿Quieres subir evidencias (fotos o pantallazos)?")) {
            while (continueUploading) {
                const success = await handleCaptureEvidence('no_show');
                if (success) {
                    uploadCount++;
                    continueUploading = window.confirm("¿Quieres subir otra evidencia?");
                } else {
                    continueUploading = false;
                }
            }
        }
        
        if (uploadCount === 0) {
            if (!window.confirm("No se han subido evidencias. ¿Deseas continuar marcando como No Show de todos modos?")) {
                return;
            }
        }
        
        const comment = window.prompt("Ingresa un comentario para el No Show (opcional):", "No Show");
        
        await handleAction(selectedTrip.id, 'no_show', { driver_notes: comment || "No Show" });
        toast.success(`Estado actualizado a No Show. (${uploadCount} evidencias subidas)`);
    };

    const handleStopNoShow = async (stop) => {
        if (!window.confirm(`¿Confirmas No Show para la parada ${stop.address}?`)) return;
        
        let uploadCount = 0;
        let continueUploading = true;
        
        if (window.confirm("¿Quieres subir evidencias (fotos o pantallazos)?")) {
            while (continueUploading) {
                const success = await handleCaptureEvidence(`no_show_stop_${stop.id}`);
                if (success) {
                    uploadCount++;
                    continueUploading = window.confirm("¿Quieres subir otra evidencia?");
                } else {
                    continueUploading = false;
                }
            }
        }
        
        if (uploadCount === 0) {
            if (!window.confirm("No se han subido evidencias. ¿Deseas continuar marcando como No Show de todos modos?")) {
                return;
            }
        }
        
        const comment = window.prompt("Ingresa un comentario (opcional):", "No Show en Parada");
        
        await handleStopAction(selectedTrip.id, stop.id, 'no_show', comment || "No Show en Parada");
        toast.success(`Parada marcada como No Show. (${uploadCount} evidencias subidas)`);
    };

    const handleStopAction = async (tripId, stopId, action, comment = null) => {
        setIsSubmittingAction(true);
        try {
            const response = await apiClient.post(`/transport/trips/${tripId}/stops/${stopId}/action`, { action, comment });
            await loadData(true);
            setSelectedTrip(response.data);
        } catch (error) {
            alert(error.response?.data?.detail || "Error al actualizar parada");
        } finally {
            setIsSubmittingAction(false);
        }
    };

    const recenterMap = () => {
        if (currentPos) {
            setIsFollowing(true);
            if (mapInstance) {
                mapInstance.panTo(currentPos);
                mapInstance.setZoom(16);
            }
        }
    };

    // Función auxiliar para generar el link de Google Calendar
    const getCalendarUrl = (trip) => {
        if (!trip) return "#";
        try {
            const startTime = new Date(trip.scheduled_time);
            if (isNaN(startTime.getTime())) return "#";
            const duration = trip.estimated_duration_minutes || 60;
            const endTime = new Date(startTime.getTime() + duration * 60000);
            
            // Formato Google: YYYYMMDDTHHmmSSZ
            const fmt = (d) => d.toISOString().replace(/-|:|\.\d\d\d/g, "");
            
            const title = encodeURIComponent(`T. Emilia: ${toTitleCase(trip.passenger_name)}`);
            
            // Ordenar paradas intermedias si existen
            const sortedStops = trip.stops ? [...trip.stops].sort((a, b) => a.order - b.order) : [];
            const stopsText = sortedStops.length > 0 
                ? sortedStops.map((stop, idx) => `🛑 PARADA INTERMEDIA ${idx + 1}: ${stop.address}`).join('\n') + '\n'
                : '';

            const details = encodeURIComponent(
                `📌 PASAJERO: ${toTitleCase(trip.passenger_name)}\n` +
                `📞 TEL: ${trip.passenger_phone || ''}\n` +
                `📍 RECOGIDA: ${trip.pickup_address}\n` +
                stopsText +
                `🏁 DESTINO: ${trip.dropoff_address}\n` +
                `📝 NOTAS: ${trip.notes || ''}`
            );
            const location = encodeURIComponent(trip.pickup_address);
            const dates = `${fmt(startTime)}/${fmt(endTime)}`;
            
            return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${details}&location=${location}`;
        } catch (e) {
            return "#";
        }
    };

    if (loading) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-dark text-white p-4">
                <Spinner animation="grow" variant="primary" className="mb-3" />
                <h6 className="fw-bold">Cargando tu Portal...</h6>
                {loadingError && (
                    <div className="mt-4 text-center animate-fade-in">
                        <Alert variant="danger" className="py-2 px-3 small border-0 shadow-sm text-start mb-3">
                            <AlertTriangle size={16} className="me-2"/>
                            {loadingError}
                        </Alert>
                        <div className="d-grid gap-2">
                            <Button variant="primary" size="sm" className="fw-bold" onClick={() => loadData()}>
                                REINTENTAR CONEXIÓN
                            </Button>
                            <Button variant="outline-light" size="sm" onClick={handleLogout}>
                                CERRAR SESIÓN
                            </Button>
                        </div>
                        <p className="mt-3" style={{ fontSize: '10px', opacity: 0.5 }}>
                            Si el error persiste, revisa tu conexión a internet o el estado del servidor.
                        </p>
                    </div>
                )}
                {!loadingError && (
                    <p className="small text-muted mt-2">Estableciendo conexión segura...</p>
                )}
            </div>
        );
    }

    // --- VISTA DE SELECCIÓN DE VEHÍCULO (BLOQUEANTE) ---
    if (showVehicleSelection) {
        return (
            <div className="vh-100 bg-dark text-white p-4 d-flex flex-column align-items-center justify-content-center">
                <Car size={64} className="text-primary mb-4 animate-bounce" />
                <h4 className="fw-bold mb-2">Comenzar Turno</h4>
                <p className="text-muted text-center small mb-4">Selecciona el móvil que vas a operar hoy para recibir servicios.</p>
                
                <Card className="w-100 bg-white bg-opacity-10 border-0 shadow-lg" style={{maxWidth: '400px'}}>
                    <Card.Body className="p-0">
                        <ListGroup variant="flush">
                            {driverInfo?.assignable_vehicles?.map(v => (
                                <ListGroup.Item 
                                    key={v.id} 
                                    action 
                                    disabled={v.is_occupied || isAssigningVehicle}
                                    onClick={() => handleAssignVehicle(v.id)}
                                    className="bg-transparent text-white border-white border-opacity-10 py-3 d-flex justify-content-between align-items-center"
                                >
                                    <div>
                                        <div className="fw-bold fs-5">Móvil {v.internal_code}</div>
                                        <small className="opacity-50">{v.plate}</small>
                                    </div>
                                    {v.is_occupied ? (
                                        <Badge bg="danger">OCUPADO</Badge>
                                    ) : (
                                        <ChevronRight className="text-primary" />
                                    )}
                                </ListGroup.Item>
                            ))}
                            {(!driverInfo?.assignable_vehicles || driverInfo.assignable_vehicles.length === 0) && (
                                <div className="p-4 text-center text-muted small italic">
                                    No tienes móviles habilitados. Contacta a soporte.
                                </div>
                            )}
                        </ListGroup>
                    </Card.Body>
                </Card>
                
                <Button variant="link" className="text-muted mt-4 text-decoration-none" onClick={handleLogout}>
                    <LogOut size={16} className="me-2"/> Salir de la aplicación
                </Button>
            </div>
        );
    }

    // --- VISTA DE DETALLE ---
    if (selectedTrip) {
        const status = selectedTrip.status.toLowerCase();
        const safePickup = (Number.isFinite(selectedTrip.pickup_lat) && Number.isFinite(selectedTrip.pickup_lng)) ? { lat: selectedTrip.pickup_lat, lng: selectedTrip.pickup_lng } : DEFAULT_CENTER;
        const safeDropoff = (Number.isFinite(selectedTrip.dropoff_lat) && Number.isFinite(selectedTrip.dropoff_lng)) ? { lat: selectedTrip.dropoff_lat, lng: selectedTrip.dropoff_lng } : DEFAULT_CENTER;

        // Paradas pendientes (aún no visitadas) ordenadas por order
        const pendingStops = selectedTrip.stops
            ? [...selectedTrip.stops]
                .filter(s => s.status === 'pending' && Number.isFinite(s.lat) && Number.isFinite(s.lng))
                .sort((a, b) => a.order - b.order)
            : [];



        // Origen del mapa integrado: posición actual del conductor (si disponible) o pickup fijo
        const mapOrigin = (status === 'on_board' && currentPos && Number.isFinite(Number(currentPos.lat)) && Number.isFinite(Number(currentPos.lng)))
            ? { lat: Number(currentPos.lat), lng: Number(currentPos.lng) }
            : safePickup;

        // Destino final del mapa integrado siempre es el dropoff
        const destination = safeDropoff;

        // Waypoints del mapa integrado: solo paradas pendientes (excluye ya visitadas)
        const mapWaypoints = (status === 'on_board')
            ? pendingStops.map(s => ({ location: { lat: s.lat, lng: s.lng }, stopover: true }))
            : selectedTrip.stops?.sort((a,b) => a.order - b.order).filter(s => Number.isFinite(s.lat) && Number.isFinite(s.lng)).map(s => ({ location: { lat: s.lat, lng: s.lng }, stopover: true })) || [];

        const getNavigationTarget = () => {
            if (status === 'en_route' || status === 'confirmed' || status === 'assigned' || status === 'pending') {
                return safePickup;
            }
            if (pendingStops.length > 0) {
                return { lat: pendingStops[0].lat, lng: pendingStops[0].lng };
            }
            return safeDropoff;
        };

        const getGoogleMapsUrl = () => {
            const target = getNavigationTarget();
            if ((status === 'on_board' || status === 'arrived') && pendingStops.length > 0) {
                let url = `https://www.google.com/maps/dir/?api=1&destination=${target.lat},${target.lng}`;
                const extraWaypoints = pendingStops.slice(1);
                if (extraWaypoints.length > 0) {
                    url += `&waypoints=${extraWaypoints.map(s => `${s.lat},${s.lng}`).join('|')}`;
                }
                return url;
            }
            return `https://www.google.com/maps/dir/?api=1&destination=${target.lat},${target.lng}`;
        };

        const getWazeUrl = () => {
            const target = getNavigationTarget();
            return `https://waze.com/ul?ll=${target.lat},${target.lng}&navigate=yes`;
        };


        return (
            <div className="bg-white d-flex flex-column overflow-hidden" style={{ height: '100dvh' }}>
                <div className="px-3 pb-3 border-bottom d-flex align-items-center bg-dark text-white shadow-sm" style={{ zIndex: 10, paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
                    <Button variant="link" className="text-white p-0 me-3" onClick={() => setSelectedTrip(null)}>⬅️</Button>
                    <div className="flex-grow-1 text-truncate">
                        <h6 className="mb-0 fw-bold">{t('service_title')} #{selectedTrip.id}</h6>
                        <div className="d-flex align-items-center gap-2 mt-1">
                            <small className="text-info">{formatDate(selectedTrip.scheduled_time, selectedTrip.timezone)} - {formatTime(selectedTrip.scheduled_time, selectedTrip.timezone)}</small>
                            <a 
                                href={getCalendarUrl(selectedTrip)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white opacity-75 hover-opacity-100 d-flex align-items-center gap-1 text-decoration-none"
                                style={{ fontSize: '11px', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '4px', padding: '1px 6px', backgroundColor: 'rgba(255,255,255,0.15)' }}
                            >
                                <Calendar size={10} /> {t('schedule_btn')}
                            </a>
                        </div>
                    </div>
                    <Badge bg={getStatusBadgeProps(status, t).bgClass} className="fw-bold px-2 py-1.5">{getStatusBadgeProps(status, t).text.toUpperCase()}</Badge>
                </div>

                {/* MAPA — Oculto por defecto, se activa con botón */}
                {!showMap ? (
                    <button
                        onClick={() => { setShowMap(true); setDirections(null); }}
                        className="w-100 border-0 d-flex align-items-center justify-content-center gap-2 fw-bold"
                        style={{
                            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                            color: 'white',
                            padding: '14px',
                            fontSize: '0.9rem',
                            letterSpacing: '0.5px',
                            cursor: 'pointer'
                        }}
                    >
                        <span style={{ fontSize: '1.2rem' }}>🗺️</span>
                        {t('view_map_route')}
                    </button>
                ) : (
                <div className="position-relative flex-shrink-0" style={{ height: '70dvh', transition: 'height 0.3s ease' }}>
                    <GoogleMap 
                        mapContainerStyle={{ width: '100%', height: '100%', position: 'absolute' }} 
                        defaultCenter={safePickup}
                        zoom={15} 
                        options={{ disableDefaultUI: true, gestureHandling: 'greedy' }}
                        onLoad={(map) => { 
                            setMapInstance(map); 
                        }}
                        onDragStart={() => setIsFollowing(false)}
                    >
                        {currentPos && <MarkerF position={{ lat: Number(currentPos.lat), lng: Number(currentPos.lng) }} icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE, scale: 7, fillColor: '#007bff', fillOpacity: 1, strokeColor: 'white', strokeWeight: 2 }} />}
                        {!directions && <DirectionsService 
                            options={{ 
                                origin: mapOrigin, 
                                destination, 
                                waypoints: mapWaypoints,
                                travelMode: 'DRIVING' 
                            }} 
                            callback={directionsCallback} 
                        />}
                        {directions && <DirectionsRenderer 
                            directions={directions} 
                            options={{ 
                                suppressMarkers: false,
                                preserveViewport: false
                            }} 
                        />}
                    </GoogleMap>
                    
                    {/* BOTÓN CERRAR MAPA */}
                    <button
                        onClick={() => setShowMap(false)}
                        className="position-absolute top-0 start-0 m-2 border-0 d-flex align-items-center gap-1 fw-bold"
                        style={{
                            background: 'rgba(0,0,0,0.75)',
                            color: 'white',
                            borderRadius: '20px',
                            padding: '6px 14px',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            zIndex: 10
                        }}
                    >
                        ✕ {t('close_map')}
                    </button>

                    {/* BOTÓN RECENTRAR */}
                    <Button 
                        variant="white" 
                        className="position-absolute top-0 end-0 m-3 rounded-circle shadow-sm border" 
                        style={{width: '45px', height: '45px', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                        onClick={recenterMap}
                    >
                        <Crosshair size={20} className="text-primary" />
                    </Button>
                </div>
                )}

                {/* CONTENEDOR INFERIOR (55% de la pantalla) */}
                <div className="flex-grow-1 bg-white shadow-lg rounded-top-4 d-flex flex-column overflow-hidden" style={{zIndex: 100, borderTop: '1px solid #eee', minHeight: 0}}>
                    
                    {/* CONTENIDO SCROLLEABLE */}
                    <div className="p-3 flex-grow-1" style={{ paddingBottom: '1rem', overflowY: 'auto', flex: '1 1 auto', WebkitOverflowScrolling: 'touch' }}>
                            {/* BANNER TIPO DE VEHICULO REQUERIDO */}
                            {renderVehicleCategoryBadge(selectedTrip, true)}

                            {/* CABECERA */}
                            <div className="d-flex flex-column gap-2 mb-3 border-bottom pb-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="fw-bold mb-0">{toTitleCase(selectedTrip.passenger_name)}</h5>
                                        <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
                                            {renderPaxBadge(selectedTrip)}
                                            {renderVehicleCategoryBadge(selectedTrip, false)}
                                            {driverInfo?.show_pricing !== false && (
                                                <span className="text-success fw-bold">${getDriverVisibleEarnings(selectedTrip).toLocaleString()}</span>
                                            )}
                                            {isTripVoucherProcessed(selectedTrip) && ((selectedTrip.waiting_time_minutes || 0) > 0 || (selectedTrip.waiting_time_provider_cost || 0) > 0) && (
                                                <Badge bg="warning" text="dark" className="py-1 px-2 d-flex align-items-center gap-1 border-0" style={{ borderRadius: '8px', fontSize: '0.78em' }}>
                                                    ⏳ Espera aprobada: {formatWaitingMinutes(selectedTrip.waiting_time_minutes)}
                                                    {driverInfo?.show_pricing !== false && (selectedTrip.waiting_time_provider_cost || 0) > 0
                                                        ? ` ($${Math.round(selectedTrip.waiting_time_provider_cost).toLocaleString()})`
                                                        : ''}
                                                </Badge>
                                            )}
                                            {selectedTrip.flight_number && (
                                                <Badge bg="info" className="text-dark py-1 px-2 d-flex align-items-center gap-1 border-0" style={{ borderRadius: '8px', fontSize: '0.8em' }}>
                                                    ✈️ {t('flight_label')}: {selectedTrip.flight_number}
                                                </Badge>
                                            )}
                                            {selectedTrip.client?.name && (
                                                <Badge bg="secondary" className="text-white py-1 px-2 d-flex align-items-center gap-1 border-0" style={{ borderRadius: '8px', fontSize: '0.8em', backgroundColor: '#6c757d' }}>
                                                    💼 {t('agreement')}: {selectedTrip.client.name}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        {['en_route', 'arrived', 'on_board'].includes(status) && (
                                            <Button 
                                                variant="dark" 
                                                className="fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                                                onClick={() => handleCaptureEvidence()}
                                                disabled={isCapturingEvidence}
                                            >
                                                {isCapturingEvidence ? <Spinner size="sm" animation="border" /> : <Camera size={18} />} Lens
                                            </Button>
                                        )}
                                        <Button 
                                            variant="danger" 
                                            size="sm" 
                                            className="rounded-circle shadow-sm" 
                                            style={{width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                            onClick={handlePanic} 
                                            disabled={isSendingPanic}
                                        >
                                            <AlertTriangle size={20} />
                                        </Button>
                                    </div>
                                </div>

                                {['assigned', 'pending'].includes(status) && (
                                    <Row className="g-2 mt-2">
                                        {status === 'assigned' ? (
                                            <>
                                                <Col xs={6}>
                                                    <Button 
                                                        variant="outline-danger" 
                                                        className="w-100 py-2.5 fw-bold" 
                                                        onClick={() => handleAction(selectedTrip.id, 'pending', {driver_id: null, is_rejection: true})}
                                                        disabled={isSubmittingAction}
                                                        style={{ opacity: isSubmittingAction ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease', borderRadius: '10px' }}
                                                    >
                                                        <XCircle size={18} className="me-1" /> {t('btn_reject')}
                                                    </Button>
                                                </Col>
                                                <Col xs={6}>
                                                    <Button 
                                                        variant="success" 
                                                        className="w-100 py-2.5 fw-bold" 
                                                        onClick={() => handleAction(selectedTrip.id, 'confirmed')}
                                                        disabled={isSubmittingAction}
                                                        style={{ opacity: isSubmittingAction ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease', borderRadius: '10px' }}
                                                    >
                                                        <Check size={18} className="me-1" /> {t('accept')}
                                                    </Button>
                                                </Col>
                                            </>
                                        ) : (
                                            <Col xs={12}>
                                                <Button 
                                                    variant="success" 
                                                    className="w-100 py-2.5 fw-bold" 
                                                    onClick={() => handleAction(selectedTrip.id, 'confirmed', {driver_id: user.linked_driver_id})}
                                                    disabled={isSubmittingAction}
                                                    style={{ opacity: isSubmittingAction ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease', borderRadius: '10px' }}
                                                >
                                                    <Check size={18} className="me-1" /> {t('btn_accept_service')}
                                                </Button>
                                            </Col>
                                        )}
                                    </Row>
                                )}
                            </div>

                            {/* LISTADO DE PASAJEROS CON WHATSAPP Y TELÉFONO */}
                            <div className="mt-2 p-2 bg-light rounded border">
                                <div className="fw-bold text-muted mb-2 px-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>{t('passenger_list')}</div>
                                    
                                    {/* Pasajero Principal */}
                                    <div className="d-flex align-items-center justify-content-between p-2 bg-white rounded mb-2 border-bottom shadow-xs flex-wrap gap-2">
                                        <div className="text-truncate" style={{ maxWidth: '55%' }}>
                                            <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.9em' }}>{toTitleCase(selectedTrip.passenger_name)}</div>
                                            <div className="small text-muted">{selectedTrip.passenger_phone || t('no_phone_short')}</div>
                                        </div>
                                        <div className="d-flex gap-1 flex-wrap justify-content-end">
                                            {isBookingComTrip(selectedTrip) && (
                                                <Button
                                                    size="sm"
                                                    className="d-flex align-items-center justify-content-center fw-bold position-relative shadow-xs"
                                                    style={{
                                                        height: '32px',
                                                        fontSize: '0.8em',
                                                        padding: '0 10px',
                                                        backgroundColor: '#003580',
                                                        borderColor: '#003580',
                                                        color: '#ffffff'
                                                    }}
                                                    onClick={(e) => openBookingChatModal(selectedTrip, e)}
                                                >
                                                    <span className="me-1">🏨</span> Chat Booking
                                                    {(bookingUnreadMap[selectedTrip.id] || bookingUnreadMap[String(selectedTrip.id)]) > 0 && (
                                                        <span
                                                            className="badge rounded-pill bg-danger ms-1"
                                                            style={{ fontSize: '0.68rem', padding: '2px 5px' }}
                                                        >
                                                            {bookingUnreadMap[selectedTrip.id] || bookingUnreadMap[String(selectedTrip.id)]}
                                                        </span>
                                                    )}
                                                </Button>
                                            )}
                                            {selectedTrip.passenger_phone && (
                                                <>
                                                    <Button 
                                                        variant="success" 
                                                        size="sm" 
                                                        className="d-flex align-items-center justify-content-center" 
                                                        style={{ height: '32px', fontSize: '0.8em', padding: '0 8px' }}
                                                        onClick={() => handleWhatsAppClick(selectedTrip.passenger_name, selectedTrip.passenger_phone, selectedTrip.scheduled_time, selectedTrip.id, selectedTrip.timezone, selectedTrip.client_id)}
                                                    >
                                                        <MessageCircle size={14} className="me-1" /> WhatsApp
                                                    </Button>
                                                    <Button 
                                                        variant="primary" 
                                                        size="sm" 
                                                        className="d-flex align-items-center justify-content-center"
                                                        style={{ height: '32px', fontSize: '0.8em', padding: '0 8px' }}
                                                        onClick={() => window.open(`tel:${selectedTrip.passenger_phone}`, '_blank')}
                                                    >
                                                        <Phone size={14} className="me-1" /> {t('btn_call')}
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Acompañantes */}
                                    {selectedTrip.additional_passengers && selectedTrip.additional_passengers.map((pax) => (
                                        <div key={pax.id} className="d-flex align-items-center justify-content-between p-2 bg-white rounded mb-1 border-bottom shadow-xs">
                                            <div className="text-truncate" style={{ maxWidth: '60%' }}>
                                                <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.9em' }}>{toTitleCase(pax.full_name)}</div>
                                                <div className="small text-muted">{pax.phone || t('no_phone_short')}</div>
                                            </div>
                                            {pax.phone && (
                                                <div className="d-flex gap-1">
                                                    <Button 
                                                        variant="success" 
                                                        size="sm" 
                                                        className="d-flex align-items-center justify-content-center" 
                                                        style={{ height: '32px', fontSize: '0.8em', padding: '0 8px' }}
                                                        onClick={() => handleWhatsAppClick(pax.full_name, pax.phone, selectedTrip.scheduled_time, selectedTrip.id, selectedTrip.timezone, selectedTrip.client_id)}
                                                    >
                                                        <MessageCircle size={14} className="me-1" /> WhatsApp
                                                    </Button>
                                                    <Button 
                                                        variant="primary" 
                                                        size="sm" 
                                                        className="d-flex align-items-center justify-content-center"
                                                        style={{ height: '32px', fontSize: '0.8em', padding: '0 8px' }}
                                                        onClick={() => window.open(`tel:${pax.phone}`, '_blank')}
                                                    >
                                                        <Phone size={14} className="me-1" /> {t('btn_call')}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>





                            {selectedTrip.extra_services && selectedTrip.extra_services.length > 0 && (
                                <Card className="border-warning mb-3 bg-warning bg-opacity-10 text-warning-emphasis border-start border-4 shadow-sm" style={{ borderRadius: '12px' }}>
                                    <Card.Body className="p-3">
                                        <div className="text-muted small fw-bold mb-2" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>{t('extra_services_title')}</div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedTrip.extra_services.map(s => {
                                                const extraIconMap = {
                                                    baby_seat:      { icon: '👶', label: 'Silla de Bebé / Booster' },
                                                    water_still:    { icon: '💧', label: 'Agua Mineral sin Gas' },
                                                    water_sparkling:{ icon: '💧', label: 'Agua Mineral con Gas' },
                                                    pet:            { icon: '🐾', label: 'Mascota Pequeña' },
                                                    extra_luggage:  { icon: '🧳', label: 'Maleta Extra' },
                                                };
                                                const info = extraIconMap[s.id] || { icon: '➕', label: s.name || s.id };
                                                return (
                                                    <Badge key={s.id} bg="warning" className="text-dark p-2 d-flex align-items-center gap-1" style={{ fontSize: '0.85em', borderRadius: '8px', border: 'none' }}>
                                                        <span style={{ fontSize: '1.1em' }}>{info.icon}</span>
                                                        <span>{info.label}</span>
                                                    </Badge>
                                                );
                                            })}
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}

                            <div className="mb-3 bg-light p-2 rounded">
                                <div className="d-flex gap-2 mb-2">
                                    <MapPin size={16} className="text-danger flex-shrink-0 mt-1" />
                                    <div className="small"><div className="text-muted" style={{fontSize: '10px'}}>{t('lbl_origin_caps')}</div><div className="fw-bold">{selectedTrip.pickup_address}</div></div>
                                </div>

                                {/* PARADAS INTERMEDIAS */}
                                {selectedTrip.stops && selectedTrip.stops.length > 0 && (
                                    <div className="d-flex flex-column gap-2 my-3">
                                        <div className="fw-bold small text-muted">{t('intermediate_stops')} ({selectedTrip.stops.length})</div>
                                        {selectedTrip.stops.sort((a,b) => a.order - b.order).map((stop, idx) => (
                                            <div key={stop.id || idx} className={`p-2 rounded border ${stop.status === 'done' ? 'bg-light border-success' : 'bg-white border-info'}`}>
                                                <div className="d-flex gap-2 mb-1">
                                                    <div className="mt-1">
                                                        {stop.status === 'done' ? <Check size={14} className="text-success fw-bold"/> : <MapPin size={14} className="text-info"/>}
                                                    </div>
                                                    <div className="small fw-bold text-dark">{stop.address}</div>
                                                </div>
                                                {stop.passenger_name && (
                                                    <div className="mt-2 ms-4 p-2 bg-light rounded d-flex justify-content-between align-items-center shadow-sm">
                                                        <span className="small fw-bold text-muted"><User size={12} className="me-1"/> {toTitleCase(stop.passenger_name)}</span>
                                                        {stop.passenger_phone && (
                                                            <div className="d-flex gap-2">
                                                                <Button variant="outline-success" size="sm" className="p-1 px-2 border-0" onClick={(e) => { e.stopPropagation(); handleWhatsAppClick(stop.passenger_name, stop.passenger_phone, selectedTrip.scheduled_time, selectedTrip.id, selectedTrip.timezone, selectedTrip.client_id); }}><MessageCircle size={16} /></Button>
                                                                <Button variant="outline-primary" size="sm" className="p-1 px-2 border-0" onClick={(e) => { e.stopPropagation(); window.open(`tel:${stop.passenger_phone}`, '_blank'); }}><Phone size={16} /></Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="d-flex gap-2">
                                    <Navigation size={16} className="text-primary flex-shrink-0 mt-1" />
                                    <div className="small"><div className="text-muted" style={{fontSize: '10px'}}>{t('lbl_destination_caps')}</div><div className="fw-bold">{selectedTrip.dropoff_address}</div></div>
                                </div>
                            </div>

                            {/* {t('general_observations')} PARA EL CHOFER */}
                            {selectedTrip.notes && (
                                <div className="mb-3 p-3 bg-warning bg-opacity-10 border border-warning border-opacity-25 rounded-3">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <FileText size={16} className="text-warning" />
                                        <span className="fw-bold small text-warning-emphasis">{t('general_observations')}</span>
                                    </div>
                                    <div className="small fw-medium text-dark">
                                        {selectedTrip.notes}
                                    </div>
                                </div>
                            )}

                            {status === 'on_board' && (
                                <div className="mb-3 p-2 bg-light rounded border">
                                    <small className="d-block fw-bold text-muted mb-1" style={{fontSize: '10px'}}>{t('trip_comments_title')}</small>
                                    <Form.Control 
                                        as="textarea" 
                                        rows={2} 
                                        placeholder={t('trip_comments_ph')} 
                                        className="border-0 bg-white" 
                                        style={{ fontSize: '16px' }}
                                        value={driverComment} 
                                        onChange={(e) => setDriverComment(e.target.value)} 
                                        onBlur={() => window.scrollTo(0, 0)}
                                    />
                                </div>
                            )}

                            {/* ACCESOS DIRECTOS DE NAVEGACIÓN MOVIDOS AL SCROLL */}
                            <div className="mt-3 pt-3 border-top">
                                <Row className="g-2">
                                    <Col xs={6}>
                                        <Button 
                                            variant="outline-primary" 
                                            className="w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 border-2 shadow-sm text-primary" 
                                            style={{ fontSize: '15px', borderRadius: '10px' }}
                                            onClick={() => window.open(getGoogleMapsUrl(), '_blank')}
                                        >
                                            🗺️ Google Maps
                                        </Button>
                                    </Col>
                                    <Col xs={6}>
                                        <Button 
                                            variant="outline-info" 
                                            className="w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 border-2 shadow-sm text-info" 
                                            style={{ fontSize: '15px', borderRadius: '10px' }}
                                            onClick={() => window.open(getWazeUrl(), '_blank')}
                                        >
                                            🚙 Waze
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        </div>

                        {/* BOTONERA FIJA (STICKY BOTTOM) */}
                        {['confirmed', 'en_route', 'arrived', 'on_board'].includes(status) && (
                            <div className="p-3 bg-white border-top shadow-sm" style={{ zIndex: 101, paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}>
                                <div className="d-grid gap-2">
                                    {status === 'confirmed' && (() => {
                                        const isTimeAllowed = isTripAllowedForEnRoute(selectedTrip.scheduled_time);
                                        return (
                                            <div className="d-flex flex-column gap-2">
                                                <Button 
                                                    variant="primary" 
                                                    className="w-100 py-2 fw-bold shadow" 
                                                    onClick={() => handleAction(selectedTrip.id, 'en_route')}
                                                    disabled={isSubmittingAction || !isTimeAllowed}
                                                    style={{ opacity: (isSubmittingAction || !isTimeAllowed) ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease' }}
                                                >
                                                    🚀 {t('btn_start_trip')}
                                                </Button>
                                                {!isTimeAllowed && (
                                                    <div className="text-center text-muted small italic" style={{ fontSize: '11px' }}>
                                                        {t('time_restriction_notice')}
                                                    </div>
                                                )}
                                                <Button 
                                                    variant="link" 
                                                    className="text-danger small text-decoration-none" 
                                                    onClick={() => { if(window.confirm(t('release_trip_confirm'))) handleAction(selectedTrip.id, 'pending', {driver_id: null}); }}
                                                    disabled={isSubmittingAction}
                                                >
                                                    {t('btn_release_trip')}
                                                </Button>
                                            </div>
                                        );
                                    })()}
                                    {status === 'en_route' && (
                                        <Button 
                                            variant="warning" 
                                            className="w-100 py-2 fw-bold shadow" 
                                            onClick={() => handleAction(selectedTrip.id, 'arrived')}
                                            disabled={isSubmittingAction}
                                            style={{ opacity: isSubmittingAction ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease' }}
                                        >
                                            📍 {t('btn_arrived')}
                                        </Button>
                                    )}
                                    {status === 'arrived' && (
                                        <div className="w-100">
                                            <div className="d-flex gap-2 w-100 mb-2">
                                                <Button 
                                                    variant="success" 
                                                    className="flex-grow-1 py-3 fw-bold shadow" 
                                                    onClick={() => handleAction(selectedTrip.id, 'on_board', selectedTrip.first_movement_at ? { start_time: selectedTrip.first_movement_at } : {})}
                                                    disabled={isSubmittingAction}
                                                    style={{ opacity: isSubmittingAction ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease', fontSize: '16px', borderRadius: '10px' }}
                                                >
                                                    👨‍👩‍👧‍👦 {t('btn_onboard')}
                                                </Button>
                                                <Button 
                                                    variant="outline-danger" 
                                                    className="py-3 fw-bold shadow" 
                                                    onClick={handleNoShow}
                                                    disabled={isSubmittingAction}
                                                    style={{ opacity: isSubmittingAction ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease', fontSize: '16px', borderRadius: '10px' }}
                                                >
                                                    🚫 NO SHOW
                                                </Button>
                                            </div>
                                            <WaitingTimer trip={selectedTrip} scheduledTimeStr={selectedTrip.scheduled_time} gpsArrivedTimeStr={selectedTrip.gps_arrived_time || new Date().toISOString()} />
                                        </div>
                                    )}
                                    {status === 'on_board' && (() => {
                                        const pendingStops = selectedTrip.stops?.filter(s => s.status !== 'done')?.sort((a,b) => a.order - b.order) || [];
                                        const currentStop = pendingStops[0];
                                        
                                        if (currentStop) {
                                            if (!currentStop.status || currentStop.status === 'pending') {
                                                return (
                                                    <Button 
                                                        variant="info" 
                                                        className="w-100 py-2 fw-bold shadow text-white" 
                                                        onClick={() => handleStopAction(selectedTrip.id, currentStop.id, 'arrived')}
                                                        disabled={isSubmittingAction}
                                                        style={{ opacity: isSubmittingAction ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease' }}
                                                    >
                                                        📍 {t('arrived_at_stop')}: {currentStop.address.substring(0, 15)}{currentStop.address.length > 15 ? '...' : ''}
                                                    </Button>
                                                );
                                            } else if (currentStop.status === 'arrived') {
                                                return (
                                                    <div className="d-flex gap-2 w-100">
                                                        <Button 
                                                            variant="success" 
                                                            className="flex-grow-1 py-2 fw-bold shadow" 
                                                            onClick={() => handleStopAction(selectedTrip.id, currentStop.id, 'done')}
                                                            disabled={isSubmittingAction}
                                                            style={{ opacity: isSubmittingAction ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease' }}
                                                        >
                                                            ▶️ {t('continue_trip_leave')}
                                                        </Button>
                                                        <Button 
                                                            variant="outline-danger" 
                                                            className="py-2 fw-bold shadow" 
                                                            onClick={() => handleStopNoShow(currentStop)}
                                                            disabled={isSubmittingAction}
                                                            style={{ opacity: isSubmittingAction ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease' }}
                                                        >
                                                            🚫 NO SHOW
                                                        </Button>
                                                    </div>
                                                );
                                            }
                                        }
                                        return (
                                            <Button 
                                                variant="danger" 
                                                className="w-100 py-2 fw-bold shadow d-flex align-items-center justify-content-center gap-2" 
                                                onClick={async () => {
                                                    if (isSubmittingAction) return;
                                                    setIsSubmittingAction(true);
                                                    try {
                                                        const notesToSubmit = driverComment || selectedTrip?.driver_notes || "";
                                                        setCompletionNotes(notesToSubmit);
                                                        
                                                        const updateData = { status: 'completed', driver_notes: notesToSubmit };
                                                        if (currentPos && Number.isFinite(currentPos.lat)) {
                                                            updateData.gps_completed_lat = currentPos.lat;
                                                            updateData.gps_completed_lng = currentPos.lng;
                                                        }
                                                        
                                                        const updatedTripData = await updateTrip(selectedTrip.id, updateData);
                                                        if (updatedTripData) {
                                                            setSelectedTrip(updatedTripData);
                                                        }
                                                        
                                                        // Enviar pulso de GPS inmediato
                                                        if (currentPos && Number.isFinite(currentPos.lat)) {
                                                            try {
                                                                await apiClient.post(`/transport/trips/${selectedTrip.id}/track`, { 
                                                                    trip_id: selectedTrip.id, 
                                                                    lat: currentPos.lat, 
                                                                    lng: currentPos.lng, 
                                                                    speed: 0, 
                                                                    heading: 0,
                                                                    timestamp: new Date().toISOString()
                                                                });
                                                            } catch (gpsError) {
                                                                console.error("Error al enviar pulso GPS inmediato:", gpsError);
                                                            }
                                                        }
                                                        
                                                        // Recargar datos
                                                        await loadData(true);
                                                        
                                                        // Abrir el resumen
                                                        setShowCompletionModal(true);
                                                    } catch (error) {
                                                        alert(error.response?.data?.detail || "Error al finalizar el servicio");
                                                    } finally {
                                                        setIsSubmittingAction(false);
                                                    }
                                                }}
                                                disabled={isSubmittingAction}
                                                style={{ opacity: isSubmittingAction ? 0.5 : 1, filter: isSubmittingAction ? 'blur(1px)' : 'none', transition: 'all 0.2s ease' }}
                                            >
                                                {isSubmittingAction ? <Spinner size="sm" animation="border" /> : null}
                                                🏁 {t('btn_finish')}
                                            </Button>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>

                {/* MODAL DE RESUMEN DE FINALIZACIÓN DE VIAJE (DENTRO DE LA VISTA DE DETALLE) */}
                {renderDriverBookingChatModal()}
                <Modal 
                    show={showCompletionModal} 
                    onHide={() => {}} 
                    backdrop="static" 
                    keyboard={false} 
                    centered 
                    contentClassName="border-0 rounded-4 shadow-lg"
                >
                    <Modal.Header className="border-0 pb-2 bg-light rounded-top-4">
                        <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2">
                            <CheckCircle className="text-success" size={24} />
                            {t('completion_summary_title')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <p className="text-muted small mb-4">{t('completion_details_subtitle')}</p>
                        
                        <div className="bg-light p-3 rounded-4 mb-4">
                            {(() => {
                                const showApprovedWait = isTripVoucherProcessed(selectedTrip) && (
                                    (selectedTrip?.waiting_time_minutes || 0) > 0 ||
                                    (selectedTrip?.waiting_time_provider_cost || 0) > 0
                                );
                                const totalVisibleEarnings = getDriverVisibleEarnings(selectedTrip);
                                const approvedWaitCost = showApprovedWait ? Math.round(selectedTrip?.waiting_time_provider_cost || 0) : 0;
                                const baseTripFare = Math.max(0, totalVisibleEarnings - approvedWaitCost);

                                return (
                                    <Row className="g-3">
                                        <Col xs={showApprovedWait ? 6 : 12}>
                                            <small className="text-muted d-block" style={{ fontSize: '10px' }}>{t('distance_covered')}</small>
                                            <strong>{selectedTrip?.distance_km ? `${Number(selectedTrip.distance_km).toFixed(1)} km` : '0.0 km'}</strong>
                                        </Col>
                                        {showApprovedWait && (
                                            <Col xs={6}>
                                                <small className="text-muted d-block" style={{ fontSize: '10px' }}>{t('waiting_time_caps')}</small>
                                                <strong>{formatWaitingMinutes(selectedTrip?.waiting_time_minutes)}</strong>
                                            </Col>
                                        )}

                                        {driverInfo?.show_pricing !== false && (
                                            <>
                                                {showApprovedWait ? (
                                                    <>
                                                        <Col xs={6}>
                                                            <small className="text-muted d-block" style={{ fontSize: '10px' }}>{t('trip_fare')}</small>
                                                            <strong>${baseTripFare.toLocaleString()}</strong>
                                                        </Col>
                                                        <Col xs={6}>
                                                            <small className="text-muted d-block" style={{ fontSize: '10px' }}>{t('waiting_payment')}</small>
                                                            <strong>${approvedWaitCost.toLocaleString()}</strong>
                                                        </Col>
                                                    </>
                                                ) : (
                                                    <Col xs={12}>
                                                        <small className="text-muted d-block" style={{ fontSize: '10px' }}>{t('trip_fare')}</small>
                                                        <strong>${totalVisibleEarnings.toLocaleString()}</strong>
                                                    </Col>
                                                )}
                                                <Col xs={12} className="border-top pt-2">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <small className="text-muted fw-bold" style={{ fontSize: '11px' }}>TOTAL A PAGAR</small>
                                                        <strong className="fs-5 text-success">${totalVisibleEarnings.toLocaleString()}</strong>
                                                    </div>
                                                </Col>
                                            </>
                                        )}
                                    </Row>
                                );
                            })()}
                        </div>

                        <Form.Group className="mb-0">
                            <Form.Label className="small fw-bold text-muted">OBSERVACIONES / COMENTARIOS</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={3} 
                                value={completionNotes} 
                                onChange={(e) => setCompletionNotes(e.target.value)} 
                                placeholder="Escribe alguna observación o comentario del servicio si aplica..."
                                className="rounded-3"
                                style={{ fontSize: '16px' }}
                                onBlur={() => window.scrollTo(0, 0)}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 bg-light rounded-bottom-4 p-3">
                        <Button 
                            variant="success" 
                            className="w-100 rounded-pill fw-bold py-2 shadow" 
                            disabled={isSubmittingAction}
                            onClick={async () => {
                                setIsSubmittingAction(true);
                                try {
                                    // 1. Guardar cualquier cambio de última hora a las observaciones en el modal
                                    const updateData = { driver_notes: completionNotes };
                                    await updateTrip(selectedTrip?.id, updateData);
                                    setDriverComment(completionNotes);
                                    
                                    // 2. Cerrar el modal y volver al listado general
                                    setShowCompletionModal(false);
                                    setSelectedTrip(null);
                                    
                                    // 3. Recargar los datos frescos
                                    await loadData(true);
                                } catch (error) {
                                    alert("Error al guardar observaciones finales: " + (error.response?.data?.detail || error.message));
                                } finally {
                                    setIsSubmittingAction(false);
                                }
                            }}
                        >
                            {isSubmittingAction ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                            OK
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }

    // --- VISTA PRINCIPAL ---
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Nombres de meses para los selectores
    const locale = language === 'pt' ? 'pt-BR' : language === 'en' ? 'en-US' : 'es-CL';
    const monthNames = Array.from({ length: 12 }, (_, i) => {
        const name = new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2024, i, 1));
        return name.charAt(0).toUpperCase() + name.slice(1);
    });

    const earningsToday = trips
        .filter(t => {
            const statusLower = t.status.toLowerCase();
            const isFinished = statusLower === 'completed' || statusLower === 'no_show' || (statusLower === 'cancelled_paid' && getDriverVisibleEarnings(t) > 0);
            return isFinished && new Date(t.end_time || t.scheduled_time).toDateString() === now.toDateString();
        })
        .reduce((sum, t) => sum + getDriverVisibleEarnings(t), 0);

    const earningsMonth = trips
        .filter(t => {
            const statusLower = t.status.toLowerCase();
            const isFinished = statusLower === 'completed' || statusLower === 'no_show' || (statusLower === 'cancelled_paid' && getDriverVisibleEarnings(t) > 0);
            if (!isFinished) return false;
            const d = new Date(t.end_time || t.scheduled_time);
            return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
        })
        .reduce((sum, t) => sum + getDriverVisibleEarnings(t), 0);

    const filteredEarningsTrips = trips
        .filter(t => {
            const statusLower = t.status.toLowerCase();
            const isFinished = statusLower === 'completed' || statusLower === 'no_show' || (statusLower === 'cancelled_paid' && (t.driver_earnings || 0) > 0);
            if (!isFinished) return false;
            const d = new Date(t.end_time || t.scheduled_time);
            if (earningsView === 'today') {
                return d.toDateString() === now.toDateString();
            } else if (earningsView === 'month') {
                return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
            }
            return false;
        })
        .sort((a, b) => new Date(b.end_time || b.scheduled_time) - new Date(a.end_time || a.scheduled_time));

    return (
        <Container fluid className="p-0 bg-light min-vh-100 d-flex flex-column">
            {/* OVERLAY ASAP (MODELO UBER) */}
            {asapOffer && offerCountdown > 0 && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3" style={{zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(10px)'}}>
                    <Card className="w-100 border-0 shadow-2xl overflow-hidden animate-bounce-in" style={{maxWidth: '450px', borderRadius: '24px'}}>
                        <div className="bg-danger p-4 text-white text-center position-relative">
                            <div className="position-absolute top-0 start-50 translate-middle-x mt-2 opacity-10"><Zap size={120} /></div>
                            <h2 className="fw-bold mb-1">¡NUEVO VIAJE ASAP!</h2>
                            <p className="small opacity-75 mb-0 text-uppercase tracking-widest">Servicio Urgente Detectado</p>
                        </div>
                        <Card.Body className="p-4 text-center">
                            <div className="d-flex justify-content-center mb-4">
                                <div className="position-relative d-flex align-items-center justify-content-center" style={{width: '100px', height: '100px'}}>
                                    <svg className="position-absolute" style={{transform: 'rotate(-90deg)', width: '100px', height: '100px'}}>
                                        <circle cx="50" cy="50" r="45" fill="none" stroke="#eee" strokeWidth="8" />
                                        <circle cx="50" cy="50" r="45" fill="none" stroke="#dc3545" strokeWidth="8" strokeDasharray="283" strokeDashoffset={283 - (283 * offerCountdown / 30)} style={{transition: 'stroke-dashoffset 1s linear'}} />
                                    </svg>
                                    <span className="fs-1 fw-bold text-danger">{offerCountdown}s</span>
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="d-flex align-items-center gap-3 mb-3 bg-light p-3 rounded-4">
                                    <div className="bg-white p-2 rounded-circle shadow-sm"><MapPin size={24} className="text-success" /></div>
                                    <div className="text-start">
                                        <small className="text-muted d-block fw-bold" style={{fontSize: '10px'}}>PUNTO DE RECOGIDA</small>
                                        <strong className="fs-6">{asapOffer.pickup_address}</strong>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-3 bg-light p-3 rounded-4">
                                    <div className="bg-white p-2 rounded-circle shadow-sm"><Navigation size={24} className="text-primary" /></div>
                                    <div className="text-start">
                                        <small className="text-muted d-block fw-bold" style={{fontSize: '10px'}}>DESTINO FINAL</small>
                                        <strong className="fs-6">{asapOffer.dropoff_address}</strong>
                                    </div>
                                </div>
                            </div>

                            {asapOffer.extra_services && asapOffer.extra_services.length > 0 && (
                                <div className="mb-4 p-3 bg-warning bg-opacity-10 border border-warning border-opacity-15 rounded-4 text-start">
                                    <small className="text-muted d-block fw-bold mb-2" style={{fontSize: '10px', letterSpacing: '0.5px'}}>EXTRAS SOLICITADOS</small>
                                    <div className="d-flex flex-wrap gap-2">
                                        {asapOffer.extra_services.map(s => {
                                            const extraIconMap = {
                                                baby_seat:      { icon: '👶', label: 'Silla de Bebé / Booster' },
                                                water_still:    { icon: '💧', label: 'Agua sin Gas' },
                                                water_sparkling:{ icon: '💧', label: 'Agua con Gas' },
                                                pet:            { icon: '🐾', label: 'Mascota' },
                                                extra_luggage:  { icon: '🧳', label: 'Maleta Extra' },
                                            };
                                            const info = extraIconMap[s.id] || { icon: '➕', label: s.name || s.id };
                                            return (
                                                <Badge key={s.id} bg="warning" className="text-dark p-2" style={{ borderRadius: '8px', fontSize: '0.8em', border: 'none' }}>
                                                    {info.icon} {info.label}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <Row className="g-3">
                                <Col xs={12}>
                                    <div className="d-flex justify-content-between align-items-center px-2 mb-2">
                                        {renderPaxBadge(asapOffer, "px-3 py-2 fs-6 rounded-pill dark")}
                                        {driverInfo?.show_pricing === true && (
                                            <span className="fs-4 fw-bold text-success">${Math.round(asapOffer.driver_earnings || 0).toLocaleString()}</span>
                                        )}
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <Button variant="outline-secondary" className="w-100 py-3 rounded-4 fw-bold border-2" onClick={() => { setOfferCountdown(0); handleAction(asapOffer.id, 'pending', {driver_id: null, vehicle_id: null, is_rejection: true, asap_status: 'searching', offered_driver_ids: [...(asapOffer.offered_driver_ids || []), user.linked_driver_id]}); }}>
                                        <XCircle size={20} className="me-2"/> RECHAZAR
                                    </Button>
                                </Col>
                                <Col xs={6}>
                                    <Button variant="danger" className="w-100 py-3 rounded-4 fw-bold shadow-lg pulse-animation" onClick={() => handleAction(asapOffer.id, 'confirmed', {asap_status: 'idle'})}>
                                        <Check size={20} className="me-2"/> ¡ACEPTAR!
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>
            )}

            <div className="text-white px-4 pb-4 shadow-sm sticky-top" style={{ background: 'linear-gradient(135deg, #1a1c23 0%, #0f1115 100%)', borderBottom: '1px solid rgba(255,255,255,0.05)', zIndex: 1020, paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div onClick={() => setShowProfile(true)} style={{cursor: 'pointer'}}>
                        <div className="d-flex align-items-center gap-2">
                            <UserCircle size={28} className="text-primary" />
                            <div>
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0">{user?.name}</h5>
                                    <span className="badge bg-secondary" style={{fontSize: '9px', opacity: 0.7}}>
                                        v{APP_VERSION} {otaVersion ? `(OTA: ${otaVersion})` : ''} {capgoError ? `(Err: ${capgoError})` : ''}
                                    </span>
                                    {user?.role === 'admin' && (
                                        <Button 
                                            variant="outline-warning" 
                                            size="sm" 
                                            className="py-0 px-1 d-flex align-items-center gap-1" 
                                            style={{fontSize: '10px'}} 
                                            onClick={(e) => { e.stopPropagation(); navigate('/'); }}
                                        >
                                            <ArrowLeftRight size={10} /> {t('admin')}
                                        </Button>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <small className="text-info"><Car size={14} /> {t('mobile')} {driverInfo?.assigned_vehicle_code || '---'}</small>
                                    <Badge 
                                        bg={pushEnabled === true ? "success" : pushEnabled === false ? "danger" : "secondary"} 
                                        className="d-flex align-items-center gap-1 cursor-pointer" 
                                        style={{fontSize:'9px', cursor: 'pointer'}}
                                        onClick={() => setShowSetupGuide(true)}
                                    >
                                        {pushEnabled === true ? <Bell size={10}/> : <BellOff size={10}/>} {pushEnabled === true ? t('push_ok') : t('configure')}
                                    </Badge>
                                    {!isOnline && driverInfo?.assigned_vehicle_code && (
                                        <Button variant="link" size="sm" className="p-0 text-warning text-decoration-none fw-bold" style={{fontSize:'10px'}} onClick={handleReleaseVehicle}>({t('change')})</Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-2">
                        <div className="bg-dark bg-opacity-25 px-2 py-1 rounded-pill d-flex align-items-center gap-2 border border-secondary border-opacity-25 flex-shrink-0">
                            <Form.Check 
                                type="switch"
                                id="online-switch"
                                checked={isOnline}
                                onChange={toggleOnlineStatus}
                                className={`m-0 ${isOnline ? 'text-success fw-bold' : 'text-warning'}`}
                                label={<span style={{fontSize: '12px', marginTop: '2px', display: 'inline-block'}}>{isOnline ? t('available') : t('paused')}</span>}
                            />
                        </div>
                    </div>
                </div>
                
                <div className="d-flex gap-2 mt-3">
                    <div 
                        className="bg-white bg-opacity-10 p-2 rounded flex-grow-1 border border-white border-opacity-10 text-center transition-all cursor-pointer"
                        style={{ cursor: 'pointer', transition: 'transform 0.1s' }}
                        onClick={() => setEarningsView('today')}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        <small className="d-block opacity-75 small" style={{fontSize: '10px'}}>{t('today')}</small>
                        {driverInfo?.show_pricing !== false ? (
                            <span className="fw-bold text-success">${earningsToday.toLocaleString()}</span>
                        ) : (
                            <span className="fw-bold text-success" style={{ fontSize: '11px' }}>{t('view_list')}</span>
                        )}
                    </div>
                    <div 
                        className="bg-white bg-opacity-10 p-2 rounded flex-grow-1 border border-white border-opacity-10 text-center transition-all cursor-pointer"
                        style={{ cursor: 'pointer', transition: 'transform 0.1s' }}
                        onClick={() => setEarningsView('month')}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        <small className="d-block opacity-75 small" style={{fontSize: '10px'}}>{t('current_month')}</small>
                        {driverInfo?.show_pricing !== false ? (
                            <span className="fw-bold text-info">${earningsMonth.toLocaleString()}</span>
                        ) : (
                            <span className="fw-bold text-info" style={{ fontSize: '11px' }}>{t('view_list')}</span>
                        )}
                    </div>
                    <div 
                        className="bg-white bg-opacity-10 p-2 rounded flex-grow-1 border border-white border-opacity-10 text-center cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowLocationDisclosure(true)}
                    >
                        <small className="d-block opacity-75 small" style={{fontSize: '10px'}}>{t('gps_permission')}</small>
                        <span className="fw-bold text-warning" style={{ fontSize: '11px' }}>{t('view_notice')}</span>
                    </div>
                </div>
            </div>

            {/* MODAL DE PERFIL */}
            <Modal show={showProfile} onHide={() => setShowProfile(false)} centered size="sm">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h6 fw-bold">{t('my_profile')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <div className="bg-primary bg-opacity-10 p-4 text-center border-bottom">
                        <div className="position-relative d-inline-block mb-2">
                            {uploadingPhoto ? (
                                <div className="d-flex align-items-center justify-content-center bg-white rounded-circle shadow-sm" style={{ width: '80px', height: '80px' }}>
                                    <Spinner animation="border" variant="primary" size="sm" />
                                </div>
                            ) : profileImageUrl ? (
                                <img 
                                    src={profileImageUrl} 
                                    alt="Foto de Perfil" 
                                    className="rounded-circle border border-3 border-white shadow-sm" 
                                    style={{ width: '80px', height: '80px', objectFit: 'cover', cursor: 'pointer' }}
                                    onClick={handleSelectPhoto}
                                />
                            ) : (
                                <UserCircle 
                                    size={80} 
                                    className="text-primary bg-white rounded-circle border border-3 border-white shadow-sm" 
                                    style={{ cursor: 'pointer' }}
                                    onClick={handleSelectPhoto} 
                                />
                            )}
                            <button 
                                className="btn btn-primary btn-sm rounded-circle position-absolute p-0 d-flex align-items-center justify-content-center shadow-sm" 
                                style={{ width: '26px', height: '26px', bottom: '0', right: '0', border: '2px solid white' }}
                                onClick={handleSelectPhoto}
                                title="Cambiar Foto"
                                disabled={uploadingPhoto}
                            >
                                <Camera size={14} />
                            </button>
                        </div>
                        <h5 className="fw-bold mb-0">{driverInfo?.full_name}</h5>
                        <Badge bg="primary">{driverInfo?.rut}</Badge>
                    </div>

                    {/* SELECTOR DE IDIOMA EN PERFIL */}
                    <div className="p-3 pb-0">
                        <div className="d-flex justify-content-between align-items-center bg-light p-2.5 rounded-3 border">
                            <div className="d-flex align-items-center gap-2">
                                <span style={{ fontSize: '1.2rem' }}>🌐</span>
                                <span className="small fw-bold text-dark">{t('language')}:</span>
                            </div>
                            <LanguageSelector />
                        </div>
                    </div>

                    <div className="p-3">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <div className="bg-light p-2 rounded-circle"><Smartphone size={20} className="text-muted" /></div>
                            <div><small className="text-muted d-block" style={{fontSize: '10px'}}>{t('phone')}</small><strong>{driverInfo?.phone || t('not_registered')}</strong></div>
                        </div>
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <div className="bg-light p-2 rounded-circle"><Mail size={20} className="text-muted" /></div>
                            <div><small className="text-muted d-block" style={{fontSize: '10px'}}>{t('email')}</small><strong className="small">{driverInfo?.email || t('not_registered')}</strong></div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <div className="bg-light p-2 rounded-circle"><User size={20} className="text-muted" /></div>
                            <div><small className="text-muted d-block" style={{fontSize: '10px'}}>{t('role')}</small><strong>{user?.role?.toUpperCase()}</strong></div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-light p-2 d-flex flex-column gap-2">
                    <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="w-100 d-flex align-items-center justify-content-center gap-2" 
                        onClick={() => {
                            setShowProfile(false);
                            navigate('/onboarding?mode=update');
                        }}
                    >
                        📂 {t('my_documents')}
                    </Button>
                    <Button 
                        variant="outline-success" 
                        size="sm" 
                        className="w-100 d-flex align-items-center justify-content-center gap-2" 
                        onClick={() => {
                            const token = localStorage.getItem('token');
                            window.open(`${apiClient.defaults.baseURL}/transport/drivers/me/contract/pdf?token=${token}`, '_blank');
                        }}
                    >
                        📄 {t('service_certificate')}
                    </Button>
                    <Button 
                        variant="outline-info" 
                        size="sm" 
                        className="w-100 d-flex align-items-center justify-content-center gap-2" 
                        onClick={() => {
                            setShowProfile(false);
                            setActiveTab('pagos');
                            loadSettlements();
                        }}
                    >
                        💰 {t('my_payments_settlements')}
                    </Button>
                    <Button 
                        variant="outline-warning" 
                        size="sm" 
                        className="w-100 d-flex align-items-center justify-content-center gap-2 text-dark fw-bold" 
                        onClick={() => {
                            setShowProfile(false);
                            setShowAvailabilityModal(true);
                        }}
                    >
                        🕒 {t('my_schedule_availability')}
                    </Button>
                    <Button variant="secondary" size="sm" className="w-100" onClick={() => setShowProfile(false)}>{t('close')}</Button>
                    <Button variant="outline-danger" size="sm" className="w-100" onClick={handleLogout}>{t('logout')}</Button>
                    <Button variant="danger" size="sm" className="w-100 mt-2 d-flex align-items-center justify-content-center gap-1 fw-bold" onClick={() => { setShowProfile(false); setShowDeleteAccountModal(true); }}>
                        <Trash2 size={14} /> {t('delete_account')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL DE DISPONIBILIDAD Y HORARIOS */}
            <DriverAvailabilityModal 
                show={showAvailabilityModal} 
                onHide={() => setShowAvailabilityModal(false)} 
            />

            {/* BARRA DE ALERTAS CRÍTICAS (40 MINUTOS) */}
            {criticalTrips.length > 0 && (
                <div className="px-3 pt-3">
                    {criticalTrips.map(ct => {
                        const isAssigned = ct.status.toLowerCase() === 'assigned';
                        return (
                            <Alert key={ct.id} variant={isAssigned ? "danger" : "warning"} className="shadow-sm d-flex align-items-center gap-2 py-2 mb-2 border-2">
                                <Timer className={isAssigned ? "animate-pulse" : ""} />
                                <div className="flex-grow-1 small">
                                    <strong>¡URGENTE!</strong> Servicio #{ct.id} inicia en {Math.round((new Date(ct.scheduled_time) - new Date()) / 60000)} min.
                                    <br/>{isAssigned ? "AÚN NO LO HAS ACEPTADO." : "DEBES INICIAR EL VIAJE AHORA."}
                                </div>
                                <Button size="sm" variant={isAssigned ? "danger" : "warning"} onClick={() => handleSelectTrip(ct)}>Ver</Button>
                            </Alert>
                        );
                    })}
                </div>
            )}

            {/* BOTÓN DE PÁNICO FLOTANTE (Solo si está Online) */}
            {isOnline && (
                <Button 
                    variant="danger" 
                    className="position-fixed bottom-0 end-0 m-4 rounded-circle shadow-lg d-flex align-items-center justify-content-center border-4 border-white" 
                    style={{width: '70px', height: '70px', zIndex: 1050}}
                    onClick={handlePanic}
                    disabled={isSendingPanic}
                >
                    <AlertTriangle size={32} />
                </Button>
            )}

            <Tab.Container activeKey={activeTab} onSelect={(key) => { setActiveTab(key); if (key === 'pagos') loadSettlements(); }}>
                <Nav variant="underline" className="bg-white px-1 border-bottom sticky-top shadow-sm flex-nowrap justify-content-around w-100" style={{top: '115px', zIndex: 10}}>
                    <Nav.Item>
                        <Nav.Link eventKey="new" className="px-2 py-3 small fw-bold d-flex align-items-center gap-1">
                            {t('tab_new')}
                            {unseenNewCount > 0 && (
                                <Badge bg="danger" pill className="pulse-animation px-1.5 py-1" style={{ fontSize: '9px', lineHeight: 1 }}>
                                    {unseenNewCount}
                                </Badge>
                            )}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="agenda" className="px-2 py-3 small fw-bold d-flex align-items-center gap-1">
                            {t('tab_agenda')}
                            {unseenAgendaCount > 0 && (
                                <Badge bg="danger" pill className="pulse-animation px-1.5 py-1" style={{ fontSize: '9px', lineHeight: 1 }}>
                                    {unseenAgendaCount}
                                </Badge>
                            )}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="tomorrow" className="px-2 py-3 small fw-bold d-flex align-items-center gap-1">
                            {t('tab_tomorrow')}
                            {tomorrowTrips.length > 0 && (
                                <Badge bg="primary" pill className="px-1.5 py-1" style={{ fontSize: '9px', lineHeight: 1 }}>
                                    {tomorrowTrips.length}
                                </Badge>
                            )}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item><Nav.Link eventKey="done" className="px-2 py-3 small fw-bold">{t('tab_history')}</Nav.Link></Nav.Item>
                </Nav>

                <Tab.Content className="p-3 flex-grow-1">
                    <Tab.Pane eventKey="pagos">
                        {loadingSettlements ? (
                            <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>
                        ) : (
                            <div className="animate-fade-in">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <Button variant="link" className="p-0 text-decoration-none text-muted" onClick={() => setActiveTab('agenda')} style={{ fontSize: '1.1rem' }}>⬅️</Button>
                                        <h6 className="fw-bold text-muted small mb-0 text-uppercase">{t('settlements_title')}</h6>
                                    </div>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Form.Select size="sm" value={settlementsMonth} onChange={(e) => setSettlementsMonth(parseInt(e.target.value))} style={{width: 'auto', fontSize: '12px'}}>
                                            {monthNames.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                            <option value={-1}>{t('all')}</option>
                                        </Form.Select>
                                        <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={loadSettlements}>{t('refresh')}</Button>
                                    </div>
                                </div>
                                
                                {(() => {
                                    const filtered = settlementsData.personal.filter(s => settlementsMonth === -1 || getSettleMonth(s.period_start) === settlementsMonth);
                                    if (filtered.length === 0) {
                                        return <div className="text-center p-4 bg-white rounded border small italic text-muted">{t('no_settlements_month')}</div>;
                                    }

                                    const monthlyTotalAmount = filtered.reduce((sum, s) => sum + (s.total_amount || 0), 0);
                                    const monthlyTotalTrips = filtered.reduce((sum, s) => sum + (s.total_trips || 0), 0);
                                    const commonMonthlyInvoice = filtered.find(s => s.invoice_number);
                                    const targetYear = getSettleYear(filtered[0]?.period_start);
                                    const targetMonth = settlementsMonth !== -1 ? settlementsMonth + 1 : (getSettleMonth(filtered[0]?.period_start) + 1);

                                    return (
                                        <>
                                            {settlementsMonth !== -1 && filtered.length > 1 && (
                                                <Card className="mb-3 border-0 shadow-sm text-white" style={{
                                                    background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                                                    borderRadius: '12px'
                                                }}>
                                                    <Card.Body className="p-3">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div>
                                                                <span className="badge bg-white text-primary fw-bold text-uppercase px-2 py-1 mb-1" style={{fontSize: '10px', letterSpacing: '0.5px'}}>
                                                                    Resumen Mensual Acumulado
                                                                </span>
                                                                <h5 className="fw-bold mb-0 text-white">
                                                                    {monthNames[settlementsMonth]} {targetYear}
                                                                </h5>
                                                                <small className="text-white-50">
                                                                    {filtered.length} liquidaciones semanales • {monthlyTotalTrips} viajes
                                                                </small>
                                                            </div>
                                                            <div className="text-end">
                                                                <small className="text-white-50 d-block" style={{fontSize: '11px'}}>
                                                                    {driverInfo?.show_pricing !== false ? 'Total a Facturar' : 'Servicios Computados'}
                                                                </small>
                                                                {driverInfo?.show_pricing !== false ? (
                                                                    <div className="fw-bold fs-4 text-warning">
                                                                        ${monthlyTotalAmount.toLocaleString()}
                                                                    </div>
                                                                ) : (
                                                                    <div className="fw-bold fs-5 text-white">{monthlyTotalTrips}</div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {driverInfo?.show_pricing !== false && (
                                                        <div className="border-top border-white border-opacity-25 pt-2 mt-2">
                                                            {commonMonthlyInvoice ? (
                                                                <div className="d-flex align-items-center justify-content-between bg-white bg-opacity-20 p-2 rounded flex-wrap gap-2">
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <span className="fs-5">📄</span>
                                                                        <div>
                                                                            <div className="fw-bold text-white small" style={{fontSize: '12px'}}>
                                                                                Factura SII Folio: #{commonMonthlyInvoice.invoice_number}
                                                                            </div>
                                                                            <div className="text-white-50" style={{fontSize: '10px'}}>
                                                                                Vinculada a las {filtered.length} semanas del mes
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex gap-2 align-items-center">
                                                                        {commonMonthlyInvoice.invoice_url && (
                                                                            <Button 
                                                                                variant="light" 
                                                                                size="sm" 
                                                                                className="fw-bold py-1 px-2 text-primary" 
                                                                                style={{fontSize: '11px'}}
                                                                                onClick={() => window.open(commonMonthlyInvoice.invoice_url, '_blank')}
                                                                            >
                                                                                Ver Factura ↗
                                                                            </Button>
                                                                        )}
                                                                        <Button
                                                                            variant="outline-light"
                                                                            size="sm"
                                                                            className="py-1 px-2 fw-bold"
                                                                            style={{fontSize: '10px'}}
                                                                            onClick={() => {
                                                                                const fileInput = document.getElementById('monthly-invoice-file-input');
                                                                                if (fileInput) fileInput.click();
                                                                            }}
                                                                        >
                                                                            Cambiar
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <span className="small text-white fw-semibold" style={{fontSize: '11px'}}>
                                                                            📄 Subir Factura Única del Mes (SII):
                                                                        </span>
                                                                        <small className="text-white-50" style={{fontSize: '10px'}}>
                                                                            Aplica a las {filtered.length} semanas
                                                                        </small>
                                                                    </div>
                                                                    {isUploadingMonthlyInvoice ? (
                                                                        <div className="text-center text-white py-2 bg-white bg-opacity-10 rounded border border-white border-opacity-25" style={{fontSize: '12px'}}>
                                                                            <Spinner animation="border" size="sm" className="me-2 text-white" style={{width:'14px', height:'14px'}} />
                                                                            Procesando documento y detectando folio con Gemini OCR...
                                                                        </div>
                                                                    ) : (
                                                                        <div className="d-flex gap-2 justify-content-end">
                                                                            <Button
                                                                                variant="light"
                                                                                size="sm"
                                                                                className="py-1 px-3 fw-bold text-primary d-flex align-items-center gap-1 shadow-sm"
                                                                                style={{fontSize: '11px'}}
                                                                                onClick={async () => {
                                                                                    if (Capacitor.isNativePlatform()) {
                                                                                        try {
                                                                                            const image = await CapCamera.getPhoto({
                                                                                                quality: 90,
                                                                                                allowEditing: false,
                                                                                                resultType: CameraResultType.Base64,
                                                                                                source: CameraSource.Camera,
                                                                                                promptLabelHeader: 'Fotografiar Factura Mensual'
                                                                                            });
                                                                                            if (image) {
                                                                                                const base64Response = await fetch(`data:image/jpeg;base64,${image.base64String}`);
                                                                                                const blob = await base64Response.blob();
                                                                                                handleUploadMonthlyInvoice(targetYear, targetMonth, blob, "monthly_invoice_photo.jpg");
                                                                                            }
                                                                                        } catch (err) {
                                                                                            if (err.message !== 'User cancelled photos app' && err.message !== 'Unable to get photo') {
                                                                                                console.error(err);
                                                                                                toast.error("Error al abrir cámara");
                                                                                            }
                                                                                        }
                                                                                    } else {
                                                                                        const cameraInput = document.getElementById('monthly-invoice-camera-input');
                                                                                        if (cameraInput) cameraInput.click();
                                                                                    }
                                                                                }}
                                                                            >
                                                                                📸 Cámara
                                                                            </Button>
                                                                            <Button
                                                                                variant="outline-light"
                                                                                size="sm"
                                                                                className="py-1 px-3 fw-bold d-flex align-items-center gap-1"
                                                                                style={{fontSize: '11px'}}
                                                                                onClick={() => {
                                                                                    const fileInput = document.getElementById('monthly-invoice-file-input');
                                                                                    if (fileInput) fileInput.click();
                                                                                }}
                                                                            >
                                                                                📁 Archivo (PDF/Img)
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            <input
                                                                type="file"
                                                                id="monthly-invoice-file-input"
                                                                accept="image/*,application/pdf"
                                                                style={{display: 'none'}}
                                                                onChange={(e) => {
                                                                    const file = e.target.files[0];
                                                                    if (file) {
                                                                        handleUploadMonthlyInvoice(targetYear, targetMonth, file, file.name);
                                                                    }
                                                                }}
                                                            />
                                                            {!Capacitor.isNativePlatform() && (
                                                                <input
                                                                    type="file"
                                                                    id="monthly-invoice-camera-input"
                                                                    accept="image/*"
                                                                    capture="environment"
                                                                    style={{display: 'none'}}
                                                                    onChange={(e) => {
                                                                        const file = e.target.files[0];
                                                                        if (file) {
                                                                            handleUploadMonthlyInvoice(targetYear, targetMonth, file, file.name);
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            )}

                                            {filtered.map(s => (
                                                <Card key={s.id} className="mb-2 border-0 shadow-sm border-start border-4 border-success">
                                                    <Card.Body className="p-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <div className="fw-bold" style={{fontSize:'14px'}}>{formatDate(s.period_start)} - {formatDate(s.period_end)}</div>
                                                                <small className="text-muted">{s.total_trips} viajes realizados</small>
                                                            </div>
                                                            <div className="text-end flex-shrink-0">
                                                                {driverInfo?.show_pricing !== false ? (
                                                                    <div className="fw-bold text-success" style={{fontSize:'16px'}}>${s.total_amount.toLocaleString()}</div>
                                                                ) : (
                                                                    <div className="fw-bold text-muted small" style={{fontSize:'12px'}}>DETALLE OK</div>
                                                                )}
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm" 
                                                                    className="py-0 px-2 mt-1 fw-bold" 
                                                                    style={{fontSize:'10px'}}
                                                                    onClick={() => {
                                                                        const tok = encodeURIComponent(localStorage.getItem('token') || '');
                                                                        window.open(`${apiClient.defaults.baseURL}/transport/settlements/${s.id}/export/pdf?token=${tok}`, '_blank');
                                                                    }}
                                                                >
                                                                    PDF
                                                                </Button>
                                                            </div>
                                                        </div>
                                                        {driverInfo?.show_pricing !== false && renderInvoiceSection(s)}
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </>
                                    );
                                })()}

                                {settlementsData.is_owner && (
                                    <div className="mt-4">
                                        <h6 className="fw-bold text-muted small mb-3 text-uppercase border-top pt-3">Liquidaciones de Flota</h6>
                                        {settlementsData.fleet_vehicles.map(v => (
                                            <div key={v.id} className="mb-4">
                                                <div className="d-flex align-items-center gap-2 mb-2 bg-dark bg-opacity-10 p-2 rounded">
                                                    <Car size={16} className="text-primary"/>
                                                    <span className="fw-bold small">Móvil {v.internal_code}</span>
                                                    <Badge bg="light" text="dark" className="ms-auto border" style={{fontSize:'10px'}}>{v.plate}</Badge>
                                                </div>
                                                {v.settlements.filter(s => settlementsMonth === -1 || getSettleMonth(s.period_start) === settlementsMonth).length === 0 ? (
                                                    <div className="p-3 bg-white bg-opacity-50 rounded border border-dashed small italic text-muted text-center">Sin pagos en este mes.</div>
                                                ) : (
                                                    v.settlements.filter(s => settlementsMonth === -1 || getSettleMonth(s.period_start) === settlementsMonth).map(s => (
                                                        <div key={s.id} className="p-2 bg-white mb-1 rounded border shadow-xs">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div className="small">
                                                                    <div className="fw-bold">{formatDate(s.period_start)}</div>
                                                                    <div className="text-muted" style={{fontSize:'10px'}}>{s.total_trips} viajes</div>
                                                                </div>
                                                                <div className="text-end">
                                                                    <div className="fw-bold small text-primary">${s.total_amount.toLocaleString()}</div>
                                                                    <Button variant="link" size="sm" className="p-0 text-info text-decoration-none" style={{fontSize:'10px'}} onClick={() => {
                                                                        const tok = encodeURIComponent(localStorage.getItem('token') || '');
                                                                        window.open(`${apiClient.defaults.baseURL}/transport/settlements/${s.id}/export/pdf?token=${tok}`, '_blank');
                                                                    }}>VER PDF</Button>
                                                                </div>
                                                            </div>
                                                            {renderInvoiceSection(s)}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="new">
                        {trips.filter(t => ['assigned', 'pending'].includes(t.status.toLowerCase())).map(trip => {
                            const diffMinutes = (new Date(trip.scheduled_time) - now) / 60000;
                            const isUrgent = diffMinutes > 0 && diffMinutes < 40;
                            return (
                            <Card key={trip.id} className={`mb-3 border-0 shadow-sm border-start border-4 ${isUrgent ? 'border-danger' : 'border-warning'}`} style={{ borderRadius: '12px', overflow: 'hidden' }} onClick={() => handleSelectTrip(trip)}>
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between mb-2 small fw-bold text-primary">
                                        <div className="d-flex align-items-center gap-2"><Calendar size={14}/> {formatDate(trip.scheduled_time, trip.timezone)} <Clock size={14}/> {formatTime(trip.scheduled_time, trip.timezone)}</div>
                                        <Badge bg={isUrgent ? "danger" : "warning"} text={isUrgent ? "white" : "dark"} className="px-2 py-1">{isUrgent ? t('urgent') : t('new_badge')}</Badge>
                                    </div>
                                    <h6 className="fw-bold">#{trip.id} {trip.flight_number ? `✈️ (${trip.flight_number})` : ''} - {toTitleCase(trip.passenger_name)}</h6>
                                    {trip.client?.name && (
                                        <div className="small text-primary mb-2 fw-semibold" style={{ fontSize: '11px' }}>
                                            💼 {t('agreement')}: {trip.client.name}
                                        </div>
                                    )}
                                    <div className="small text-truncate text-muted mb-1"><span className="text-success fw-bold">🟢</span> {trip.pickup_address}</div>
                                    <div className="small text-truncate text-muted mb-2"><span className="text-danger fw-bold">🏁</span> {trip.dropoff_address}</div>
                                    
                                    {trip.notes && (
                                        <div className="mb-2 p-2 bg-warning bg-opacity-10 rounded border border-warning border-opacity-10 small italic">
                                            <strong>{t('lbl_notes')}:</strong> {trip.notes.length > 60 ? trip.notes.substring(0, 60) + '...' : trip.notes}
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-between align-items-center mt-2 border-top pt-2">
                                        <div className="d-flex align-items-center gap-2">
                                            {renderPaxBadge(trip)}
                                            {renderVehicleCategoryBadge(trip, false)}
                                            {trip.extra_services && trip.extra_services.length > 0 && (
                                                <div className="d-flex align-items-center gap-1 border rounded px-2 py-0.5 bg-warning bg-opacity-10 text-warning-emphasis" style={{ fontSize: '12px' }}>
                                                    {trip.extra_services.map(s => {
                                                        const extraIconMap = {
                                                            baby_seat:      '👶',
                                                            water_still:    '💧',
                                                            water_sparkling:'💧',
                                                            pet:            '🐾',
                                                            extra_luggage:  '🧳',
                                                        };
                                                        return (
                                                            <span key={s.id} title={s.name || s.id}>
                                                                {extraIconMap[s.id] || '➕'}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-primary small fw-bold">{t('accept_trip_card')} <ChevronRight size={16} /></div>
                                    </div>
                                </Card.Body>
                            </Card>
                        )})}
                        {trips.filter(t => ['assigned', 'pending'].includes(t.status.toLowerCase())).length === 0 && (
                            <div className="text-center p-5 text-muted small">
                                <Bell size={32} className="mb-2 opacity-25" />
                                <p>{t('waiting_new_services')}</p>
                            </div>
                        )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="agenda">
                        {trips.filter(t => ['confirmed', 'en_route', 'arrived', 'on_board'].includes(t.status.toLowerCase())).map(trip => {
                            const isActive = ['en_route', 'arrived', 'on_board'].includes(trip.status.toLowerCase());
                            return (
                                <Card key={trip.id} className={`mb-3 border-0 shadow-sm ${isActive ? 'border-start border-4 border-primary' : ''}`} style={{ borderRadius: '12px', overflow: 'hidden', backgroundColor: isActive ? '#f0f7ff' : '#fff' }} onClick={() => handleSelectTrip(trip)}>
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between mb-2 small fw-bold text-primary">
                                            <span>{formatDate(trip.scheduled_time, trip.timezone)} - {formatTime(trip.scheduled_time, trip.timezone)}</span>
                                            <Badge bg={getStatusBadgeProps(trip.status, t).bgClass}>{getStatusBadgeProps(trip.status, t).text.toUpperCase()}</Badge>
                                        </div>
                                        <h6 className="fw-bold mb-1">#{trip.id} {trip.flight_number ? `✈️ (${trip.flight_number})` : ''} - {toTitleCase(trip.passenger_name)}</h6>
                                     {trip.client?.name && (
                                         <div className="small text-primary mb-2 fw-semibold" style={{ fontSize: '11px' }}>
                                             💼 {t('agreement')}: {trip.client.name}
                                         </div>
                                     )}
                                        <div className="small text-truncate text-muted mb-1"><span className="text-success fw-bold">🟢</span> {trip.pickup_address}</div>
                                        <div className="small text-truncate text-muted"><span className="text-danger fw-bold">🏁</span> {trip.dropoff_address}</div>
                                        <div className="mt-2 border-top pt-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                {renderPaxBadge(trip)}
                                                {renderVehicleCategoryBadge(trip, false)}
                                                {isBookingComTrip(trip) && (
                                                    <Button
                                                        size="sm"
                                                        className="py-0.5 px-2 fw-bold d-inline-flex align-items-center gap-1 shadow-xs"
                                                        style={{
                                                            backgroundColor: '#003580',
                                                            borderColor: '#003580',
                                                            color: '#ffffff',
                                                            fontSize: '11px',
                                                            borderRadius: '6px'
                                                        }}
                                                        onClick={(e) => openBookingChatModal(trip, e)}
                                                    >
                                                        <span>🏨 Chat Booking</span>
                                                        {(bookingUnreadMap[trip.id] || bookingUnreadMap[String(trip.id)]) > 0 && (
                                                            <span className="badge rounded-pill bg-danger" style={{ fontSize: '9px' }}>
                                                                {bookingUnreadMap[trip.id] || bookingUnreadMap[String(trip.id)]}
                                                            </span>
                                                        )}
                                                    </Button>
                                                )}
                                                {trip.extra_services && trip.extra_services.length > 0 && (
                                                    <div className="d-flex align-items-center gap-1 border rounded px-2 py-0.5 bg-warning bg-opacity-10 text-warning-emphasis" style={{ fontSize: '12px' }}>
                                                        {trip.extra_services.map(s => {
                                                            const extraIconMap = {
                                                                baby_seat:      '👶',
                                                                water_still:    '💧',
                                                                water_sparkling:'💧',
                                                                pet:            '🐾',
                                                                extra_luggage:  '🧳',
                                                            };
                                                            return (
                                                                <span key={s.id} title={s.name || s.id}>
                                                                    {extraIconMap[s.id] || '➕'}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                            {isActive && <div className="text-primary animate-pulse small fw-bold">{t('trip_in_progress')}</div>}
                                        </div>
                                    </Card.Body>
                                </Card>
                            );
                        })}
                        {trips.filter(t => ['confirmed', 'en_route', 'arrived', 'on_board'].includes(t.status.toLowerCase())).length === 0 && (
                            <div className="text-center p-5 text-muted small">
                                <Calendar size={32} className="mb-2 opacity-25" />
                                <p>{t('no_confirmed_trips_agenda')}</p>
                            </div>
                        )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="tomorrow">
                        {/* Banner de Confirmación / Lectura de Agenda */}
                        {tomorrowTrips.length > 0 && tomorrowAck.was_sent && tomorrowAck.target_date === tomorrowDateStr && (
                            !tomorrowAck.acknowledged ? (
                                <Card className="mb-3 border-danger bg-light" style={{ borderRadius: '12px', borderLeft: '4px solid #dc3545', overflow: 'hidden' }}>
                                    <Card.Body className="p-3 d-flex flex-column gap-2 animate-fade-in">
                                        <div className="fw-bold text-danger small d-flex align-items-center gap-1" style={{ fontSize: '11px' }}>
                                            🔔 {t('agenda_pending_confirm')}
                                        </div>
                                        <div className="small text-muted" style={{ fontSize: '12px' }}>
                                            {language === 'pt' 
                                                ? `Você tem ${tomorrowTrips.length} ${tomorrowTrips.length === 1 ? 'corrida agendada' : 'corridas agendadas'} para amanhã. Por favor, confirme que as revisou.`
                                                : language === 'en'
                                                ? `You have ${tomorrowTrips.length} scheduled ${tomorrowTrips.length === 1 ? 'ride' : 'rides'} for tomorrow. Please confirm you reviewed them.`
                                                : `Tienes ${tomorrowTrips.length} ${tomorrowTrips.length === 1 ? 'servicio programado' : 'servicios programados'} para mañana. Por favor, confirma que los revisaste.`
                                            }
                                        </div>
                                        <Button 
                                            variant="danger" 
                                            size="sm" 
                                            className="fw-bold text-uppercase"
                                            style={{ fontSize: '11px' }}
                                            disabled={isAcknowledgeSubmitting}
                                            onClick={handleConfirmTomorrowAgenda}
                                        >
                                            {isAcknowledgeSubmitting ? <Spinner animation="border" size="sm" /> : t('confirm_agenda_reading')}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            ) : (
                                <Card className="mb-3 border-success bg-light" style={{ borderRadius: '12px', borderLeft: '4px solid #28a745', overflow: 'hidden' }}>
                                    <Card.Body className="p-2.5 d-flex align-items-center gap-2 animate-fade-in">
                                        <span style={{ fontSize: '1.2rem' }}>✅</span>
                                        <div className="small flex-grow-1">
                                            <span className="fw-bold text-success" style={{ fontSize: '12px' }}>{t('agenda_confirmed')}</span>
                                            <div className="text-muted" style={{ fontSize: '10px' }}>
                                                {t('agenda_confirmed_on')} {new Date(tomorrowAck.acknowledged_at).toLocaleDateString(locale)} {t('at_time_hrs')} {new Date(tomorrowAck.acknowledged_at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })} hrs.
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            )
                        )}

                        {tomorrowTrips.map(trip => {
                            const mainPaxName = trip.passenger_name || "";
                            const mainPaxPhone = trip.passenger_phone || "";
                            const additionalPassengers = trip.additional_passengers || [];
                            const stops = trip.stops || [];
                            
                            return (
                                <Card key={trip.id} className="mb-3 border-0 shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fff' }}>
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between mb-2 small fw-bold text-primary">
                                            <span>
                                                {formatDate(trip.scheduled_time, trip.timezone)} - {formatTime(trip.scheduled_time, trip.timezone)} hrs
                                            </span>
                                            <Badge bg="primary">{t('status_scheduled')}</Badge>
                                        </div>
                                        <h6 className="fw-bold mb-1">#{trip.id} {trip.flight_number ? `✈️ (${trip.flight_number})` : ''}</h6>
                                        {trip.client?.name && (
                                            <div className="small text-primary mb-2 fw-semibold" style={{ fontSize: '11px' }}>
                                                💼 {t('agreement')}: {trip.client.name}
                                            </div>
                                        )}
                                        <div className="small text-truncate text-muted mb-1"><span className="text-success fw-bold">🟢</span> {trip.pickup_address}</div>
                                        <div className="small text-truncate text-muted mb-3"><span className="text-danger fw-bold">🏁</span> {trip.dropoff_address}</div>
                                        
                                        <div className="border-top pt-2">
                                            <div className="fw-bold text-muted mb-2" style={{fontSize:'12px'}}>📋 {t('passenger_list')}:</div>
                                            
                                            {/* Principal Passenger */}
                                            <div className="d-flex align-items-center justify-content-between py-1 border-bottom">
                                                <div className="small flex-grow-1">
                                                    <strong>{toTitleCase(mainPaxName)}</strong>
                                                    <span className="badge bg-secondary ms-2" style={{fontSize: '9px'}}>{t('pax_principal')}</span>
                                                    <div className="text-muted" style={{fontSize: '11px'}}>{mainPaxPhone || t('no_phone')}</div>
                                                </div>
                                                {mainPaxPhone && (
                                                    <Button 
                                                        variant="success" 
                                                        size="sm" 
                                                        className="d-flex align-items-center gap-1 py-1 px-2"
                                                        style={{fontSize: '12px'}}
                                                        onClick={(e) => { e.stopPropagation(); handleWhatsAppClick(mainPaxName, mainPaxPhone, trip.scheduled_time, trip.id, trip.timezone, trip.client_id); }}
                                                    >
                                                        <MessageCircle size={14} /> WhatsApp
                                                    </Button>
                                                )}
                                            </div>
                                            
                                            {/* Additional Passengers */}
                                            {additionalPassengers.map((p, idx) => (
                                                <div key={p.id || idx} className="d-flex align-items-center justify-content-between py-1 border-bottom">
                                                    <div className="small flex-grow-1">
                                                        <strong>{toTitleCase(p.full_name)}</strong>
                                                        <span className="badge bg-info text-dark ms-2" style={{fontSize: '9px'}}>{t('pax_companion')}</span>
                                                        <div className="text-muted" style={{fontSize: '11px'}}>{p.phone || t('no_phone')}</div>
                                                    </div>
                                                    {p.phone && (
                                                        <Button 
                                                            variant="success" 
                                                            size="sm" 
                                                            className="d-flex align-items-center gap-1 py-1 px-2"
                                                            style={{fontSize: '12px'}}
                                                            onClick={(e) => { e.stopPropagation(); handleWhatsAppClick(p.full_name, p.phone, trip.scheduled_time, trip.id, trip.timezone, trip.client_id); }}
                                                        >
                                                            <MessageCircle size={14} /> WhatsApp
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                            
                                            {/* Stop Passengers */}
                                            {stops.map((s, idx) => {
                                                const hasPax = s.passenger_name && s.passenger_name.trim().length > 0;
                                                if (!hasPax) return null;
                                                return (
                                                    <div key={s.id || idx} className="d-flex align-items-center justify-content-between py-1 border-bottom">
                                                        <div className="small flex-grow-1">
                                                            <strong>{toTitleCase(s.passenger_name)}</strong>
                                                            <span className="badge bg-warning text-dark ms-2" style={{fontSize: '9px'}}>{t('stop_word')} {idx + 1}</span>
                                                            <div className="text-muted" style={{fontSize: '11px'}}>{s.passenger_phone || t('no_phone')}</div>
                                                        </div>
                                                        {s.passenger_phone && (
                                                            <Button 
                                                                variant="success" 
                                                                size="sm" 
                                                                className="d-flex align-items-center gap-1 py-1 px-2"
                                                                style={{fontSize: '12px'}}
                                                                onClick={(e) => { e.stopPropagation(); handleWhatsAppClick(s.passenger_name, s.passenger_phone, trip.scheduled_time, trip.id, trip.timezone, trip.client_id); }}
                                                            >
                                                                <MessageCircle size={14} /> WhatsApp
                                                            </Button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Card.Body>
                                </Card>
                            );
                        })}
                        {tomorrowTrips.length === 0 && (
                            <div className="text-center p-5 text-muted small bg-white rounded shadow-sm">
                                <Calendar size={32} className="mb-2 opacity-25" />
                                <p>{t('no_trips_tomorrow')}</p>
                            </div>
                        )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="done">
                        <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                            <h6 className="fw-bold text-muted small mb-0 text-uppercase">{t('trips_history_title')}</h6>
                            <Form.Select size="sm" value={historyMonth} onChange={(e) => setHistoryMonth(parseInt(e.target.value))} style={{width: 'auto', fontSize: '12px'}}>
                                {monthNames.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                <option value={-1}>{t('all')}</option>
                            </Form.Select>
                        </div>
                        {loadingHistory ? (
                            <div className="text-center p-4 text-muted small bg-white rounded shadow-sm">
                                <Spinner size="sm" animation="border" variant="primary" className="me-2" />
                                {t('loading_month_history')}
                            </div>
                        ) : (() => {
                            const historyTrips = trips.filter(t => {
                                const statusLower = (t.status || '').toLowerCase();
                                const isFinished = statusLower === 'completed' || statusLower === 'no_show' || (statusLower === 'cancelled_paid' && (t.driver_earnings || 0) > 0);
                                return isFinished && (historyMonth === -1 || getChileMonth(t.scheduled_time) === historyMonth);
                            }).sort((a, b) => new Date(b.scheduled_time) - new Date(a.scheduled_time));

                            if (historyTrips.length === 0) {
                                return (
                                    <div className="text-center p-4 text-muted small bg-white rounded shadow-sm">
                                        {t('no_history_this_month')}
                                    </div>
                                );
                            }

                            return historyTrips.map(trip => (
                                <div key={trip.id} className={`d-flex justify-content-between align-items-center p-3 bg-white mb-2 rounded shadow-sm border-start border-3 ${trip.status.toLowerCase() === 'no_show' ? 'border-warning' : trip.status.toLowerCase() === 'cancelled_paid' ? 'border-danger' : 'border-success'}`} onClick={() => handleSelectTrip(trip)}>
                                    <div className="small">
                                        <div className="fw-bold">{formatDate(trip.scheduled_time, trip.timezone)}</div>
                                        <div className="text-muted">
                                            #{trip.id} - {toTitleCase(trip.passenger_name)} 
                                            {trip.status.toLowerCase() === 'no_show' && <span className="badge bg-warning text-dark ms-1" style={{fontSize: '9px'}}>No Show</span>}
                                            {trip.status.toLowerCase() === 'cancelled_paid' && <span className="badge bg-danger text-white ms-1" style={{fontSize: '9px'}}>{t('status_cancelled_paid')}</span>}
                                        </div>
                                        {trip.client?.name && (
                                            <div className="small text-primary fw-semibold mt-1" style={{ fontSize: '11px' }}>
                                                💼 {t('agreement')}: {trip.client.name}
                                            </div>
                                        )}
                                        {trip.extra_services && trip.extra_services.length > 0 && (
                                            <div className="d-flex gap-1 mt-1 mb-1">
                                                {trip.extra_services.map(s => {
                                                    const extraIconMap = {
                                                        baby_seat:      '👶',
                                                        water_still:    '💧',
                                                        water_sparkling:'💧',
                                                        pet:            '🐾',
                                                        extra_luggage:  '🧳',
                                                    };
                                                    return (
                                                        <span key={s.id} style={{ fontSize: '11px' }} title={s.name || s.id}>
                                                            {extraIconMap[s.id] || '➕'}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {trip.driver_notes && <div className="text-info" style={{fontSize: '10px'}}><FileText size={10}/> {trip.driver_notes}</div>}
                                    </div>
                                    {driverInfo?.show_pricing !== false && (
                                        <div className="fw-bold text-success">${getDriverVisibleEarnings(trip).toLocaleString()}</div>
                                    )}
                                </div>
                            ));
                        })()}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            {/* MODAL DE GUÍA DE CONFIGURACIÓN */}
            <SetupGuideModal show={showSetupGuide} onHide={() => setShowSetupGuide(false)} />

            {/* MODAL DE PERMISOS NATIVOS (NUEVO AUTOMÁTICO) */}
            <PermissionsCheckModal show={showPermissionsModal} onActivate={handleActivatePermissions} />

            {/* MODAL DE NOTIFICACIÓN DE PRIMER PLANO PREMIUM */}
            <Modal show={!!inAppAlert} onHide={() => setInAppAlert(null)} centered contentClassName="border-0 rounded-4 shadow-lg">
                <Modal.Header closeButton className="border-0 pb-0 bg-light rounded-top-4">
                    <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2">
                        <Bell className="text-primary" size={24} />
                        <span>{t('important_notice')}</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    {inAppAlert?.title && <h5 className="fw-bold text-dark mb-2">{inAppAlert.title}</h5>}
                    <p className="text-muted mb-0 fs-6">{inAppAlert?.body}</p>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center pb-4">
                    <Button variant="primary" className="px-5 py-2 rounded-pill fw-bold shadow-sm" onClick={() => setInAppAlert(null)}>
                        {t('understood')}
                    </Button>
                </Modal.Footer>
            </Modal>
 
            {/* MODAL ESPECIAL INTERACTIVO: PASAJEROS A BORDO */}
            <Modal show={!!confirmBoardTrip} centered backdrop="static" contentClassName="border-0 rounded-4 shadow-lg">
                <Modal.Body className="p-4 text-center">
                    <div className="fs-1 mb-3 animate__animated animate__pulse animate__infinite">👋</div>
                    <h4 className="fw-bold text-dark mb-3">{t('pax_on_board_question')}</h4>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center pb-4 gap-3">
                    <Button 
                        variant="success" 
                        className="px-5 py-2 rounded-pill fw-bold shadow-sm" 
                        onClick={async () => {
                            if (confirmBoardTrip) {
                                const { tripId, firstMovementAt } = confirmBoardTrip;
                                setConfirmBoardTrip(null);
                                await handleAction(tripId, 'on_board', { start_time: firstMovementAt });
                            }
                        }}
                    >
                        {t('yes')}
                    </Button>
                    <Button 
                        variant="danger" 
                        className="px-5 py-2 rounded-pill fw-bold shadow-sm" 
                        onClick={() => setConfirmBoardTrip(null)}
                    >
                        {t('no')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL DETALLE DE INGRESOS MENSUALES/DIARIOS */}
            <Modal show={!!earningsView} onHide={() => setEarningsView(null)} centered size="md" contentClassName="border-0 rounded-4 shadow-lg">
                <Modal.Header closeButton className="border-0 pb-2 bg-light rounded-top-4">
                    <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2">
                        <FileText className="text-info" size={22} />
                        <div>
                            <span className="fs-5 d-block">
                                {earningsView === 'today' ? t('trips_today') : t('trips_month')}
                            </span>
                            <small className="text-muted fw-normal" style={{ fontSize: '11px' }}>
                                {earningsView === 'today' ? formatDate(new Date().toISOString()) : `${monthNames[currentMonth]} ${currentYear}`}
                            </small>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3 overflow-auto" style={{ maxHeight: '70vh' }}>
                    {filteredEarningsTrips.length === 0 ? (
                        <div className="text-center p-5 text-muted small">
                            <Calendar size={32} className="mb-2 opacity-25" />
                            <p>{t('no_trips_period')}</p>
                        </div>
                    ) : (
                        <ListGroup variant="flush" className="gap-2">
                            {filteredEarningsTrips.map(trip => (
                                <ListGroup.Item 
                                    key={trip.id} 
                                    className="p-3 bg-light rounded-4 border-0 hover-scale cursor-pointer transition-all d-flex justify-content-between align-items-center"
                                    style={{ transition: 'transform 0.2s', cursor: 'pointer' }}
                                    onClick={() => {
                                        setEarningsView(null);
                                        handleSelectTrip(trip);
                                    }}
                                >
                                    <div className="text-start me-2">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span className="badge bg-info bg-opacity-10 text-info px-2 py-1 rounded-3 fw-bold" style={{ fontSize: '10px' }}>
                                                #{trip.id}
                                            </span>
                                            <span className="text-muted fw-bold" style={{ fontSize: '11px' }}>
                                                {(() => {
                                                    const dateObj = new Date(trip.scheduled_time);
                                                    const day = String(dateObj.getDate()).padStart(2, '0');
                                                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                                                    const hours = String(dateObj.getHours()).padStart(2, '0');
                                                    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                                                    return `${day}/${month} ${hours}:${minutes}`;
                                                })()}
                                            </span>
                                        </div>
                                        <div className="fw-bold text-dark mb-1" style={{ fontSize: '13px' }}>
                                            {toTitleCase(trip.passenger_name)}
                                        </div>
                                        <div className="text-muted text-truncate" style={{ fontSize: '11px', maxWidth: '250px' }}>
                                            {trip.pickup_address} → {trip.dropoff_address}
                                        </div>
                                    </div>
                                    {driverInfo?.show_pricing !== false && (
                                        <div className="text-end">
                                            <span className="fw-bold text-success fs-5">
                                                ${getDriverVisibleEarnings(trip).toLocaleString()}
                                            </span>
                                        </div>
                                    )}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-2 bg-light rounded-bottom-4 d-flex justify-content-between align-items-center p-3">
                    {driverInfo?.show_pricing !== false ? (
                        <div>
                            <small className="text-muted d-block" style={{ fontSize: '10px' }}>{t('total_period')}</small>
                            <strong className="fs-4 text-info">
                                ${(earningsView === 'today' ? earningsToday : earningsMonth).toLocaleString()}
                            </strong>
                        </div>
                    ) : (
                        <div />
                    )}
                    <Button variant="outline-secondary" className="rounded-pill px-4 fw-bold" onClick={() => setEarningsView(null)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL DE DIVULGACIÓN DESTACADA DE UBICACIÓN EN SEGUNDO PLANO (REQUERIDO POR GOOGLE PLAY Y APPLE APP STORE) */}
            <Modal show={showLocationDisclosure} onHide={() => {}} backdrop="static" keyboard={false} centered className="shadow-lg">
                <Modal.Body className="p-4 text-center">
                    <div className="mb-3 d-inline-flex p-3 rounded-circle bg-primary bg-opacity-10 text-primary">
                        <MapPin size={40} />
                    </div>
                    <h5 className="fw-bold text-dark mb-2">{t('gps_usage_title')}</h5>
                    <p className="text-muted small mb-3">
                        {t('gps_disclosure_text')}
                    </p>
                    <Alert variant="info" className="text-start small p-2 mb-3">
                        <strong>{t('gps_background_title')}</strong><br/>
                        {t('gps_background_desc')}
                    </Alert>
                    <Button variant="primary" size="lg" className="w-100 fw-bold py-2 shadow-sm" onClick={handleAcceptGpsDisclosure}>
                        {t('understood_and_continue')}
                    </Button>
                </Modal.Body>
            </Modal>
            {renderDriverBookingChatModal()}
            <DeleteAccountModal show={showDeleteAccountModal} onHide={() => setShowDeleteAccountModal(false)} />
        </Container>
    );
};

const DriverPortalWithLang = (props) => (
    <LanguageProvider>
        <DriverPortal {...props} />
    </LanguageProvider>
);

export default DriverPortalWithLang;
